import "../../styles/planner.css";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import Layout from "../Layout.tsx";
import { TerminalCall } from "../../types/TerminalCall";
import { Vessel } from "../../types/Vessel";
import { Container } from "../../types/Container";
import { showModal } from "../../data/modal";
import Modal from "../Modal.tsx";
import TerminalCallDetail from "../partial/TerminalCallDetail";
import VesselLoadStability from "../partial/VesselLoadStability";
import VesselLoadStabilityDocument from "../partial/VesselLoadStabilityDocument";
import VoyagePicker from "../FormFields/RenderVoyagePicker.tsx";
import {
  createSelectedContainer,
  updateSelectedContainer,
} from "../../data/selectedcontainers.js";
import {
  addContainerLabelFilterValue,
  addContainerTypeFilterValue,
  addSelectedTerminalCallFilterValue,
  addVesselFilterValue,
  clearContainerLabelAndTypeFilters,
  removeContainerLabelFilterValue,
  removeContainerTypeFilterValue,
} from "../../data/filter.tsx";
import {
  selectedFilteredTerminalCall,
  selectedFilteredVoyage,
} from "../../selectors/filterSelector.js";
import { useForm } from "react-hook-form";

import {
  containerUidFormatter,
  findObjectById,
  formatDateToString,
  getTerminalCallArrivalStateContainerIds,
  getTerminalCallLoadingContainerIds,
  groupTerminalCalls,
  textColorHandler,
} from "../Helpers.tsx";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  PictureAsPdfSharp as PictureAsPdfSharpIcon,
  Email as EmailIcon,
  RotateLeft as RotateLeftIcon,
  PlaylistAdd as PlaylistAddIcon,
  FilterAlt as FilterAltIcon,
  Tune as TuneIcon,
  SwapHorizontalCircle as SwapHorizontalCircleIcon,
  AltRoute as AltRouteIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Scale as ScaleIcon,
  DirectionsBoat as DirectionsBoatIcon,
  North as LoadingIcon,
  South as UnloadingIcon,
  WarningAmberRounded as WarningAmberIcon,
  BalanceOutlined as BalanceOutlinedIcon,
  DirectionsBoatOutlined as DirectionsBoatOutlinedIcon,
  ViewColumnOutlined as ViewColumnOutlinedIcon,
  VerticalAlignTopOutlined as VerticalAlignTopOutlinedIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
} from "@mui/icons-material";

import { useInitialPlannerData } from "../hooks/useInitialPlannerData.tsx";
import LabelDots from "../partial/LabelDots.tsx";
import { ContainerUploadField } from "../FormFields/RenderContainerUploadFileField.tsx";
import { showDialog } from "../../data/dialog.tsx";
import Dialog from "../Dialog.tsx";
import { getSelectedTerminalCalls } from "../../data/selectedterminalcalls.js";
import { normalizeId, normalizeIds } from "../utils/normalize.tsx";
import { useRefreshVoyageData } from "../hooks/useRefreshVoyageData.tsx";
import SideWeightIndicator from "../settings/vessel/SideWeightDistribution.tsx";
import {
  containerGroupedByAxis,
  getHighestDraughtForWaterType,
} from "../utils/vesselWeightHelpers.tsx";
import {
  getDepartureStabilityState,
  getVesselStackHeight,
} from "../utils/vesselState.tsx";
import ContainerSearch from "../partial/PlannerContainerSearch.tsx";
import FilterMenu from "../partial/FilterMenu.tsx";
import FilterChips from "../partial/FilterChips.tsx";
import { ContainerType } from "../../types/ContainerType.ts";
import PlannerViewSettings from "../partial/PlannerViewSettings.tsx";
import ShifterArrowConnector from "../partial/ShifterArrowConnector.tsx";
import PlannerViewSwitch from "../PlannerViewSwitch.tsx";
import AutoPlannerDialog from "./AutoPlannerDialog.tsx";
import {
  calculateCohortSpaceRequirement,
  evaluateAutoPlannerRules,
  getContainerGridSpan,
  getContainerWeightInTonnes,
  getSupportingContainerLayouts,
  getTeuStackSupportKind,
  avoidsBlockingEarlierDischarge,
  respectsAdnSegregation,
} from "./autoPlannerRules.ts";
import {
  getUsableVesselSlots,
  normalizeVesselPlannerSetting,
} from "./plannerSettings.ts";

const NO_CONTAINER_COLOR = "#FFFFFF";
const DEFAULT_CONTAINER_COLOR = "#FFE4C4";
const PLANNED_BADGE_COLOR = "#1976d2";
const WRAPPER_BORDER_RADIUS = "10px !important";

const TERMINAL_LIST_BORDER_RADIUS = "12px";
const DEFAULT_TERMINAL_COLOR = "#dbeafe";

const TERMINAL_LOADING_COLOR = "#ca8a04";
const TERMINAL_LOADING_BACKGROUND = "rgba(234, 179, 8, 0.13)";
const TERMINAL_LOADING_BORDER = "rgba(234, 179, 8, 0.24)";

const TERMINAL_UNLOADING_COLOR = "#16a34a";
const TERMINAL_UNLOADING_BACKGROUND = "rgba(22, 163, 74, 0.08)";
const TERMINAL_UNLOADING_BORDER = "rgba(22, 163, 74, 0.18)";

const LOADING_CONTAINER_COLOR = "loading_color";
const UNLOADING_CONTAINER_COLOR = "unloading_color";
const PLAN_TYPE_LOADING_ONLY = "loading_only";
const PLAN_TYPE_SHIFTERS = "shifters";
const PLAN_TYPE_LOADING = "loadingTerminalCall";
const PLAN_TYPE_UNLOADING = "unloadingTerminalCall";
const DOCKING_SIDE_STARBOARD = "starboard";
const DOCKING_SIDE_PORT = "port";

const SETTINGS_CHIP_SX = {
  width: "100%",
  height: "auto",
  minHeight: 26,
  justifyContent: "flex-start",
  color: "inherit",
  bgcolor: "rgba(255,255,255,0.16)",
  borderRadius: "8px",
  px: 0.75,
  py: 0.4,
  "& .MuiChip-label": {
    px: 0.5,
    fontSize: "0.66rem",
    fontWeight: 650,
    lineHeight: 1.3,
    whiteSpace: "normal",
    textAlign: "left",
    overflow: "visible",
  },
  "& .MuiChip-icon": {
    ml: 0,
    mr: 0.5,
    flexShrink: 0,
  },
};

const PLANNER_SWITCH_SX = {
  height: 40,
  p: 0.35,
  borderRadius: "10px",
  backgroundColor: "#ffffff",
  border: "1px solid rgba(15, 23, 42, 0.12)",
  overflow: "hidden",
  "& .MuiToggleButtonGroup-grouped": {
    m: 0,
    border: "0 !important",
    borderRadius: "8px !important",
    px: 1.25,
    minWidth: 96,
    height: 32,
    gap: 0.75,
    fontSize: "0.68rem",
    fontWeight: 900,
    color: "text.primary",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    transition:
      "background-color 120ms ease, color 120ms ease, border-color 120ms ease",
    "&.Mui-selected": {
      color: "#ffffff",
      backgroundColor: "#2563eb",
      "&:hover": {
        backgroundColor: "#1d4ed8",
      },
    },
    "&:hover": {
      backgroundColor: "rgba(37, 99, 235, 0.06)",
    },
    "&.Mui-disabled": {
      opacity: 0.55,
    },
  },
};

const safeAlpha = (
  color: string | undefined | null,
  opacity: number,
  fallback = DEFAULT_CONTAINER_COLOR
) => {
  try {
    return alpha(color || fallback, opacity);
  } catch {
    return alpha(fallback, opacity);
  }
};

const isMenuPdfPlanType = (nextPlanType: string) => {
  return ![PLAN_TYPE_LOADING_ONLY, PLAN_TYPE_UNLOADING].includes(nextPlanType);
};

const removeTodaySuffix = (key: string) => {
  return key
    .replace(/\s*\(today\)\s*/gi, "")
    .replace(/\s*\(Vandaag\)\s*/gi, "")
    .replace(/\s*\(Aujourd’hui\)\s*/gi, "")
    .replace(/\s*\(Aujourd'hui\)\s*/gi, "")
    .replace(/\s*\(Heute\)\s*/gi, "")
    .trim();
};

const getTerminalCallGroupSortTime = (key: string) => {
  const cleanKey = removeTodaySuffix(key);
  const [day, month, year] = cleanKey.split("/").map(Number);

  if (!day || !month || !year) {
    return Number.MAX_SAFE_INTEGER;
  }

  return new Date(year, month - 1, day).getTime();
};

type TerminalCallOverviewStatsProps = {
  terminalCall: any;
  allContainers: any[];
  selectedVesselForPlanner: any;
  t: any;
};

const normalizeApiId = (value: any) => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value;
  }

  return value["@id"] ?? null;
};

const resolveContainerFromCollection = (
  containerRef: any,
  containerById: Map<string, any>
) => {
  const id = normalizeApiId(containerRef);

  if (id && containerById.has(id)) {
    return containerById.get(id);
  }

  return containerRef;
};

const uniqueContainersById = (containers: any[]) => {
  const seen = new Set<string>();
  const uniqueContainers: any[] = [];

  containers.forEach((container: any) => {
    const id = normalizeApiId(container);

    if (!id) {
      uniqueContainers.push(container);
      return;
    }

    if (seen.has(id)) {
      return;
    }

    seen.add(id);
    uniqueContainers.push(container);
  });

  return uniqueContainers;
};

const sumContainerTeu = (containers: any[]) => {
  return containers.reduce((total, container) => {
    const teu = Number(container?.containerType?.teu ?? 0);

    return total + (Number.isFinite(teu) ? teu : 0);
  }, 0);
};

const sumContainerWeightInTonnes = (containers: any[]) => {
  return containers.reduce((total, container) => {
    const weight = Number(container?.weight ?? 0);

    if (!Number.isFinite(weight) || weight <= 0) {
      return total;
    }

    return total + weight / 1000;
  }, 0);
};

const formatStatsNumber = (value: number, decimals = 0) => {
  if (!Number.isFinite(value)) {
    return decimals === 0 ? "0" : "0.00";
  }

  return new Intl.NumberFormat("nl-BE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

const getStatsFromContainers = (containers: any[]) => {
  return {
    teu: sumContainerTeu(containers),
    containers: containers.length,
    weight: sumContainerWeightInTonnes(containers),
  };
};

const getTerminalCallContainerCounts = (terminalCall: any) => {
  return {
    unloading: Array.isArray(terminalCall?.unloadingContainers)
      ? terminalCall.unloadingContainers.length
      : 0,
    loading: Array.isArray(terminalCall?.loadingContainers)
      ? terminalCall.loadingContainers.length
      : 0,
  };
};

const getPlannerTerminalCallColor = (terminalCall?: any) => {
  return terminalCall?.terminal?.color || DEFAULT_TERMINAL_COLOR;
};

const getPlannerTerminalCallNumber = (terminalCall?: any) => {
  return (
    terminalCall?.reference ||
    terminalCall?.code ||
    terminalCall?.name ||
    terminalCall?.terminal?.abbreviation ||
    normalizeId(terminalCall?.["@id"]) ||
    "—"
  );
};

const getPlannerTerminalCallName = (terminalCall?: any) => {
  return (
    terminalCall?.terminal?.name ||
    terminalCall?.terminal?.abbreviation ||
    terminalCall?.terminal?.city ||
    "No name"
  );
};

const getPlannerTerminalStatusLabel = (
  terminalCall: any,
  terminalCallDone: boolean,
  primaryLabel: any,
  t: any
) => {
  const terminalStatus = String(terminalCall?.status || "").toLowerCase();

  if (terminalCallDone) {
    return t("completed");
  }

  if (primaryLabel?.name) {
    return primaryLabel.name;
  }

  if (terminalStatus === "planned") {
    return t("planned");
  }

  return terminalCall?.status
    ? t(terminalCall.status, terminalCall.status)
    : t("planned");
};

const TeuIcon = () => {
  return (
    <Box
      sx={{
        width: 28,
        height: 20,
        border: "2px solid currentColor",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.55rem",
        fontWeight: 950,
        lineHeight: 1,
      }}
    >
      TEU
    </Box>
  );
};

const ContainerStackIcon = () => {
  return (
    <Box
      sx={{
        width: 30,
        height: 20,
        border: "2px solid currentColor",
        borderRadius: "2px",
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: "1.5px",
        padding: "2px",
        boxSizing: "border-box",
      }}
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "currentColor",
            borderRadius: "1px",
          }}
        />
      ))}
    </Box>
  );
};

const TerminalCallContainerCounts = ({
  unloading,
  loading,
}: {
  unloading?: number | null;
  loading?: number | null;
}) => {
  const showUnloadingCount = Number(unloading ?? 0) > 0;
  const showLoadingCount = Number(loading ?? 0) > 0;

  return (
    <Stack spacing={0.3} sx={{ width: 58, flexShrink: 0 }}>
      <Box
        sx={{
          height: 24,
          display: "grid",
          gridTemplateColumns: "22px 1fr",
          alignItems: "center",
          borderRadius: "7px",
          overflow: "hidden",
          border: `1px solid ${TERMINAL_UNLOADING_BORDER}`,
          backgroundColor: TERMINAL_UNLOADING_BACKGROUND,
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "grid",
            placeItems: "center",
            color: TERMINAL_UNLOADING_COLOR,
            backgroundColor: "rgba(255,255,255,0.72)",
            borderRight: `1px solid ${TERMINAL_UNLOADING_BORDER}`,
          }}
        >
          <LoadingIcon sx={{ fontSize: 17 }} />
        </Box>

        <Typography
          noWrap
          sx={{
            px: 0.35,
            fontSize: "0.72rem",
            fontWeight: 950,
            lineHeight: 1,
            textAlign: "center",
            color: "text.primary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {showUnloadingCount ? unloading : ""}
        </Typography>
      </Box>

      <Box
        sx={{
          height: 24,
          display: "grid",
          gridTemplateColumns: "22px 1fr",
          alignItems: "center",
          borderRadius: "7px",
          overflow: "hidden",
          border: `1px solid ${TERMINAL_LOADING_BORDER}`,
          backgroundColor: TERMINAL_LOADING_BACKGROUND,
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "grid",
            placeItems: "center",
            color: TERMINAL_LOADING_COLOR,
            backgroundColor: "rgba(255,255,255,0.74)",
            borderRight: `1px solid ${TERMINAL_LOADING_BORDER}`,
          }}
        >
          <UnloadingIcon sx={{ fontSize: 17 }} />
        </Box>

        <Typography
          noWrap
          sx={{
            px: 0.35,
            fontSize: "0.72rem",
            fontWeight: 950,
            lineHeight: 1,
            textAlign: "center",
            color: "text.primary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {showLoadingCount ? loading : ""}
        </Typography>
      </Box>
    </Stack>
  );
};

const TerminalCallOverviewStats = ({
  terminalCall,
  allContainers,
  selectedVesselForPlanner,
  t,
}: TerminalCallOverviewStatsProps) => {
  const containerById = useMemo(() => {
    const map = new Map<string, any>();

    (allContainers || []).forEach((container: any) => {
      const id = normalizeApiId(container);

      if (id) {
        map.set(id, container);
      }
    });

    return map;
  }, [allContainers]);

  const unloadingContainers = useMemo(() => {
    return (terminalCall?.unloadingContainers || []).map((container: any) =>
      resolveContainerFromCollection(container, containerById)
    );
  }, [terminalCall, containerById]);

  const unloadingContainerIds = useMemo(() => {
    return new Set(
      unloadingContainers
        .map((container: any) => normalizeApiId(container))
        .filter(Boolean)
    );
  }, [unloadingContainers]);

  const loadingContainers = useMemo(() => {
    return (terminalCall?.loadingContainers || []).map((container: any) =>
      resolveContainerFromCollection(container, containerById)
    );
  }, [terminalCall, containerById]);

  const arrivalContainers = useMemo(() => {
    const arrivalIds = getTerminalCallArrivalStateContainerIds(terminalCall);

    return arrivalIds
      .map((id: string) => containerById.get(id))
      .filter(Boolean);
  }, [terminalCall, containerById]);

  const onboardContainers = useMemo(() => {
    return uniqueContainersById([
      ...loadingContainers,
      ...arrivalContainers,
    ]).filter((container: any) => {
      const id = normalizeApiId(container);

      if (!id) {
        return true;
      }

      return !unloadingContainerIds.has(id);
    });
  }, [loadingContainers, arrivalContainers, unloadingContainerIds]);

  const unloadingStats = getStatsFromContainers(unloadingContainers);
  const loadingStats = getStatsFromContainers(loadingContainers);
  const onboardStats = getStatsFromContainers(onboardContainers);

  const columns = [
    {
      key: "unloading",
      label: t("unloading"),
      subtitle: t("release"),
      color: "#16a34a",
      background:
        "linear-gradient(180deg, rgba(22,163,74,0.12) 0%, rgba(22,163,74,0.025) 100%)",
      icon: <LoadingIcon sx={{ fontSize: 22 }} />,
      stats: unloadingStats,
      tooltip: `${t("unloading")} (${t("release")})`,
    },
    {
      key: "loading",
      label: t("loading"),
      subtitle: t("add"),
      color: "#ca8a04",
      background:
        "linear-gradient(180deg, rgba(234,179,8,0.18) 0%, rgba(234,179,8,0.04) 100%)",
      icon: <UnloadingIcon sx={{ fontSize: 22 }} />,
      stats: loadingStats,
      tooltip: `${t("loading")} (${t("add")})`,
    },
    {
      key: "onboard",
      label: t("afterVisit"),
      subtitle: t("onBoard"),
      color: "#1565c0",
      background:
        "linear-gradient(180deg, rgba(21,101,192,0.12) 0%, rgba(21,101,192,0.03) 100%)",
      icon: <DirectionsBoatIcon sx={{ fontSize: 25 }} />,
      stats: onboardStats,
      tooltip: `${t("afterVisit")} (${t("onBoard")})`,
    },
  ];

  const rows = [
    {
      key: "teu",
      label: t("teu"),
      icon: <TeuIcon />,
      getValue: (stats: any) => formatStatsNumber(stats.teu, 0),
    },
    {
      key: "containers",
      label: t("containers"),
      icon: <ContainerStackIcon />,
      getValue: (stats: any) => formatStatsNumber(stats.containers, 0),
    },
    {
      key: "weight",
      label: t("weight"),
      icon: <ScaleIcon sx={{ fontSize: 23 }} />,
      getValue: (stats: any) => `${formatStatsNumber(stats.weight, 2)} t`,
    },
  ];

  return (
    <Box
      sx={{
        mt: 0.75,
        width: "100%",
        borderRadius: WRAPPER_BORDER_RADIUS,
        overflow: "hidden",
        border: "1px solid rgba(15, 23, 42, 0.1)",
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(72px, 0.8fr) repeat(3, minmax(0, 1fr))",
          borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        }}
      >
        <Box
          sx={{
            minHeight: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 0.5,
            borderRight: "1px solid rgba(15, 23, 42, 0.08)",
          }}
        >
          <Tooltip title={t("overview")} arrow>
            <Typography
              noWrap
              sx={{
                maxWidth: "100%",
                fontWeight: 950,
                fontSize: "0.58rem",
                textTransform: "uppercase",
                color: "text.primary",
              }}
            >
              {t("overview")}
            </Typography>
          </Tooltip>
        </Box>

        {columns.map((column) => (
          <Box
            key={column.key}
            sx={{
              minWidth: 0,
              minHeight: 56,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.1,
              px: 0.4,
              textAlign: "center",
              background: column.background,
              borderRight:
                column.key === "onboard"
                  ? "none"
                  : "1px solid rgba(15, 23, 42, 0.08)",
              color: column.color,
            }}
          >
            {column.icon}

            <Tooltip title={column.tooltip} arrow>
              <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                <Typography
                  noWrap
                  sx={{
                    mt: 0.1,
                    maxWidth: "100%",
                    fontSize: "0.52rem",
                    fontWeight: 950,
                    textTransform: "uppercase",
                    lineHeight: 1,
                  }}
                >
                  {column.label}
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    maxWidth: "100%",
                    fontSize: "0.45rem",
                    fontWeight: 850,
                    textTransform: "uppercase",
                    lineHeight: 1,
                    opacity: 0.85,
                  }}
                >
                  ({column.subtitle})
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        ))}
      </Box>

      {rows.map((row, rowIndex) => (
        <Box
          key={row.key}
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(72px, 0.8fr) repeat(3, minmax(0, 1fr))",
            minHeight: 46,
            borderBottom:
              rowIndex === rows.length - 1
                ? "none"
                : "1px solid rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.45,
              borderRight: "1px solid rgba(15, 23, 42, 0.08)",
              color: "text.secondary",
              "& svg": {
                fontSize: "21px",
              },
              "& > div": {
                transform: "scale(0.76)",
              },
            }}
          >
            <Tooltip title={row.label} arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                }}
              >
                {row.icon}
              </Box>
            </Tooltip>
          </Box>

          {columns.map((column) => (
            <Box
              key={`${row.key}-${column.key}`}
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.35,
                textAlign: "center",
                backgroundColor:
                  column.key === "unloading"
                    ? "rgba(22,163,74,0.02)"
                    : column.key === "loading"
                    ? "rgba(234,179,8,0.035)"
                    : "rgba(21,101,192,0.02)",
                borderRight:
                  column.key === "onboard"
                    ? "none"
                    : "1px solid rgba(15, 23, 42, 0.08)",
              }}
            >
              <Typography
                noWrap
                sx={{
                  maxWidth: "100%",
                  fontSize: row.key === "weight" ? "0.84rem" : "1.02rem",
                  fontWeight: 950,
                  lineHeight: 1,
                  color: column.color,
                }}
              >
                {row.getValue(column.stats)}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

const ContainerPlanner = () => {
  useInitialPlannerData();

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { control, register } = useForm();

  const draggedContainer = useRef<any>({});

  const [dockingSide, setDockingSide] = useState(DOCKING_SIDE_PORT);
  const [containerData, setContainerData] = useState<any[]>([]);
  const [planType, setPlanType] = useState(PLAN_TYPE_LOADING);
  const [containerColorType, setContainerColorType] = useState(
    UNLOADING_CONTAINER_COLOR
  );
  const [snackbar, setSnackbar] = useState<any>(null);
  const [isUnpositioning, setIsUnpositioning] = useState(false);
  const [appliedPlannerSettings, setAppliedPlannerSettings] = useState<any>(
    null
  );
  const [selectedVessel, setSelectedVessel] = useState<any>(undefined);
  const [selectedVesselForPlanner, setSelectedVesselForPlanner] =
    useState<any>(undefined);
  const [stabilityOpen, setStabilityOpen] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [autoPlacing, setAutoPlacing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const autoPlaceCancelledRef = useRef(false);
  const [positionModeEnabled, setPositionModeEnabled] = useState(false);
  const [containerGridLabelSwitch, setContainerGridLabelSwitch] =
    useState(false);
  const [showPositionWeights, setShowPositionWeights] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null
  );
  const [shiftingModeEnabled, setShiftingModeEnabled] = useState(false);
  const [terminalColumnDragOver, setTerminalColumnDragOver] = useState(false);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<HTMLElement | null>(null);
  const [stabilityPreviewOpen, setStabilityPreviewOpen] = useState(false);
  const [documentLang, setDocumentLang] = useState(i18n.language);

  const eventSourceRef = useRef<EventSource | null>(null);
  const stabilityPdfRef = useRef<HTMLDivElement | null>(null);
  const refreshVoyageData = useRefreshVoyageData();

  const handleCloseSnackbar = () => setSnackbar(null);
  const toggleStability = () => setStabilityOpen((open) => !open);
  const closeStability = () => setStabilityOpen(false);

  const handleExportStabilityPdf = useCallback(async () => {
    const htmlContainer = stabilityPdfRef.current;

    if (!htmlContainer) {
      return;
    }

    const now = new Date();
    const dateStr = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;

    const pdf = new jsPDF("p", "mm", "a4", true);
    const pdfPageWidth = pdf.internal.pageSize.getWidth();
    const pdfPageHeight = pdf.internal.pageSize.getHeight();

    const canvas = await html2canvas(htmlContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      scrollX: -window.scrollX,
      scrollY: -window.scrollY,
    });

    const imgData = canvas.toDataURL("image/png");
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const padding = 10;
    const availableWidth = pdfPageWidth - padding * 2;
    const availableHeight = pdfPageHeight - padding * 2;

    const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
    const imgX = (pdfPageWidth - imgWidth * ratio) / 2;
    const imgY = (pdfPageHeight - imgHeight * ratio) / 2;

    pdf.addImage(imgData, "PNG", imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    pdf.save(`stability_${dateStr}.pdf`);
  }, []);

  const openModal = useCallback(
    (opts: any) => dispatch(showModal(opts)),
    [dispatch]
  );

  const openDialog = useCallback(
    (opts: any) => dispatch(showDialog(opts)),
    [dispatch]
  );

  const { voyage } = useSelector(selectedFilteredVoyage);
  const filteredTerminalCall = useSelector(selectedFilteredTerminalCall);

  const {
    containers,
    terminals,
    terminalCalls: { data: terminalCalls },
    containerTypes,
    labels,
    vessels,
    voyages,
    filter: { container: activeFilters },
  } = useSelector((state: any) => ({
    terminalCalls: state.selectedTerminalCalls,
    vessels: state.vessels,
    containers: state.selectedContainers,
    terminals: state.terminals,
    containerTypes: state.containerTypes,
    labels: state.labels,
    voyages: state.voyages,
    filter: state.filter,
  }), (a, b) =>
    a.terminalCalls === b.terminalCalls && a.vessels === b.vessels &&
    a.containers === b.containers && a.terminals === b.terminals &&
    a.containerTypes === b.containerTypes && a.labels === b.labels &&
    a.voyages === b.voyages && a.filter === b.filter
  );

  // refreshVoyageData clears the terminal call filter, which makes the
  // selector fall back to the first terminal call in the list. After any
  // background refresh triggered from the planner, re-pin the planner to the
  // terminal call the user was working on so it never jumps away.
  const stayOnSelectedTerminalCall = useCallback(() => {
    const selectedId = filteredTerminalCall?.["@id"];
    if (!selectedId) {
      return;
    }

    const refreshedCall = findObjectById(terminalCalls, selectedId);
    if (refreshedCall) {
      dispatch(addSelectedTerminalCallFilterValue(refreshedCall));
    }
  }, [filteredTerminalCall, terminalCalls, dispatch]);

  const { labels: filterLabels, types: filterTypes } = activeFilters;
  const prevFiltersRef = useRef(activeFilters);

  useEffect(() => {
    prevFiltersRef.current = activeFilters;
  }, [activeFilters]);

  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
    };
  }, []);

  const handleOpenFilters = (e: React.MouseEvent<HTMLElement>) =>
    setFilterAnchor(e.currentTarget);

  const handleOpenSettings = (e: React.MouseEvent<HTMLElement>) =>
    setSettingsAnchor(e.currentTarget);

  const handleApplyFilters = () => {
    const prev = prevFiltersRef.current;
    const next = activeFilters;

    prev.labels
      .filter((id: string) => !next.labels.includes(id))
      .forEach((id: string) => dispatch(removeContainerLabelFilterValue(id)));

    next.labels
      .filter((id: string) => !prev.labels.includes(id))
      .forEach((id: string) => dispatch(addContainerLabelFilterValue(id)));

    prev.types
      .filter((id: string) => !next.types.includes(id))
      .forEach((id: string) => dispatch(removeContainerTypeFilterValue(id)));

    next.types
      .filter((id: string) => !prev.types.includes(id))
      .forEach((id: string) => dispatch(addContainerTypeFilterValue(id)));

    setFilterAnchor(null);
  };

  const handleCloseSettings = () => {
    setSettingsAnchor(null);
  };

  const handleDeleteFilter = (filterType: "labels" | "types", id: string) => {
    if (filterType === "labels") {
      dispatch(removeContainerLabelFilterValue(id));
      return;
    }

    dispatch(removeContainerTypeFilterValue(id));
  };

  const terminalCallData = useMemo(
    () => groupTerminalCalls(terminalCalls || []),
    [terminalCalls]
  );

  const todayKey = useMemo(() => {
    return formatDateToString(new Date(), "DD/MM/YYYY");
  }, []);

  const isTodayGroup = useCallback(
    (key: string) => {
      const cleanedKey = removeTodaySuffix(key);
      const translatedToday = String(t("today") || "").toLowerCase();

      return (
        cleanedKey.includes(todayKey) ||
        key.toLowerCase().includes("today") ||
        (translatedToday.length > 0 &&
          key.toLowerCase().includes(translatedToday))
      );
    },
    [todayKey, t]
  );

  const terminalCallGroups = useMemo(() => {
    return Object.entries(terminalCallData || {}).sort(([keyA], [keyB]) => {
      return (
        getTerminalCallGroupSortTime(keyA) - getTerminalCallGroupSortTime(keyB)
      );
    });
  }, [terminalCallData]);

  const [sameTimeDialogOpen, setSameTimeDialogOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showUnpositionConfirm, setShowUnpositionConfirm] = useState(false);
  const [showAutoPositionConfirm, setShowAutoPositionConfirm] = useState(false);
  const [showStabilityReorgConfirm, setShowStabilityReorgConfirm] =
    useState(false);
  const stabilityReorgResolveRef = useRef<((confirmed: boolean) => void) | null>(
    null
  );
  const [autoPositionAlertDismissed, setAutoPositionAlertDismissed] =
    useState(false);
  const [timeOffsets, setTimeOffsets] = useState<Record<string, number>>({});

  const getTerminalCallDateTimeKey = useCallback((tc: any) => {
    const date = tc?.planned || tc?.arrival || tc?.operationStart;
    if (!date) return null;
    const parsed = dayjs(date);
    return parsed.isValid() ? String(parsed.valueOf()) : null;
  }, []);

  const sameDateTimeWarnings = useMemo(() => {
    const byDateTime = new Map<string, any[]>();
    const warnings: Record<string, { count: number; dateLabel: string }> = {};
    const allCalls = Object.values(terminalCallData || {}).flat();
    allCalls.forEach((tc: any) => {
      const key = getTerminalCallDateTimeKey(tc);
      if (!key) return;
      const list = byDateTime.get(key) || [];
      list.push(tc);
      byDateTime.set(key, list);
    });
    byDateTime.forEach((calls) => {
      if (calls.length < 2) return;
      const dateLabel = dayjs(calls[0]?.planned || calls[0]?.arrival).format("DD MMM YYYY HH:mm");
      calls.forEach((tc: any) => {
        warnings[tc["@id"]] = { count: calls.length, dateLabel };
      });
    });
    return warnings;
  }, [terminalCallData, getTerminalCallDateTimeKey]);

  const duplicateGroups = useMemo(() => {
    const byDateTime = new Map<string, any[]>();
    const allCalls = Object.values(terminalCallData || {}).flat();
    allCalls.forEach((tc: any) => {
      const key = getTerminalCallDateTimeKey(tc);
      if (!key) return;
      const list = byDateTime.get(key) || [];
      list.push(tc);
      byDateTime.set(key, list);
    });
    const groups: Array<{ dateLabel: string; calls: any[] }> = [];
    byDateTime.forEach((calls) => {
      if (calls.length < 2) return;
      const dateLabel = dayjs(calls[0]?.planned || calls[0]?.arrival).format("DD MMM YYYY HH:mm");
      groups.push({ dateLabel, calls });
    });
    return groups;
  }, [terminalCallData, getTerminalCallDateTimeKey]);

  const hasDuplicateTimings = duplicateGroups.length > 0;

  useEffect(() => {
    if (sameTimeDialogOpen) {
      const offsets: Record<string, number> = {};
      duplicateGroups.forEach((group) => {
        group.calls.forEach((tc, ci) => {
          if (ci > 0) offsets[tc["@id"]] = ci * 10;
        });
      });
      setTimeOffsets(offsets);
    }
  }, [sameTimeDialogOpen, duplicateGroups]);

  const getTerminalCallGroupTitle = useCallback((key: string) => {
    return removeTodaySuffix(key);
  }, []);

  const isTerminalCallDone = useCallback((terminalCall: any) => {
    return Boolean(terminalCall?.departure);
  }, []);

  const getTerminalCallPrimaryLabel = useCallback((terminalCall: any) => {
    if (!Array.isArray(terminalCall?.labels) || terminalCall.labels.length === 0) {
      return null;
    }

    return terminalCall.labels[0];
  }, []);

  const mainVessels = useMemo(
    () => vessels.data.filter((v: any) => !v.barge),
    [vessels]
  );

  const getUpdatedTerminalCall = () => {
    if (filteredTerminalCall) {
      const terminalCall = findObjectById(
        terminalCalls,
        filteredTerminalCall["@id"]
      );

      return terminalCall ? { ...terminalCall } : undefined;
    }

    return undefined;
  };

  const shiftedContainers = useMemo(() => {
    const updatedTerminalCall = filteredTerminalCall;

    if (!updatedTerminalCall) {
      return [];
    }

    const terminalCallId = updatedTerminalCall["@id"];

    return (containers.data || []).filter((container: any) => {
      if (!container.shiftedDate) {
        return false;
      }

      const matchesSelf =
        container.loadingTerminalCall?.["@id"] === terminalCallId ||
        container.unloadingTerminalCall?.["@id"] === terminalCallId;

      if (matchesSelf) {
        return true;
      }

      if (container.shifterOriginal) {
        const original = container.shifterOriginal;
        const matchesOriginal =
          original.loadingTerminalCall?.["@id"] === terminalCallId ||
          original.unloadingTerminalCall?.["@id"] === terminalCallId;

        if (matchesOriginal) {
          return true;
        }
      }

      if (container.shifter) {
        const shifter = container.shifter;
        const matchesShifter =
          shifter.loadingTerminalCall?.["@id"] === terminalCallId ||
          shifter.unloadingTerminalCall?.["@id"] === terminalCallId;

        if (matchesShifter) {
          return true;
        }
      }

      return false;
    });
  }, [containers.data, filteredTerminalCall]);

  useEffect(() => {
    if (!selectedVessel && vessels.data?.length > 0) {
      const mainVessel = mainVessels[0];

      setSelectedVessel(mainVessel);
      setSelectedVesselForPlanner(mainVessel);
      dispatch(addVesselFilterValue(mainVessel));

      return;
    }

    if (
      selectedVesselForPlanner &&
      filteredTerminalCall &&
      terminalCalls?.length > 0
    ) {
      const currentTerminalCall = getUpdatedTerminalCall();

      if (!currentTerminalCall) {
        setContainerData([]);
        return;
      }

      let loadingContainerIds: any[] = [];

      setContainerData([]);

      if (planType === PLAN_TYPE_SHIFTERS) {
        loadingContainerIds = shiftedContainers;
        setDockingSide(DOCKING_SIDE_PORT);
      }

      if (
        planType === PLAN_TYPE_LOADING ||
        planType === PLAN_TYPE_LOADING_ONLY
      ) {
        loadingContainerIds =
          getTerminalCallLoadingContainerIds(currentTerminalCall);
      }

      if (planType === PLAN_TYPE_UNLOADING) {
        loadingContainerIds = Array.isArray(
          (currentTerminalCall as any).unloadingContainers
        )
          ? (currentTerminalCall as any).unloadingContainers
          : [];
      }

      if (loadingContainerIds.length > 0) {
        const foundContainers = containers.data.filter(
          (container: any) =>
            loadingContainerIds.some(
              (idObj: any) => idObj["@id"] === container["@id"]
            ) ||
            (planType === PLAN_TYPE_LOADING &&
              getTerminalCallArrivalStateContainerIds(
                currentTerminalCall
              ).includes(container["@id"]))
        );

        const sortedContainers = foundContainers
          .map((container: any) => ({ ...container }))
          .sort((a: any, b: any) => {
            const weightA = a.weight ?? 0;
            const weightB = b.weight ?? 0;

            if (weightA !== weightB) {
              return weightB - weightA;
            }

            const uidA = a.uid ?? "";
            const uidB = b.uid ?? "";
            const uidComparison = uidA.localeCompare(uidB);

            if (uidComparison !== 0) {
              return uidComparison;
            }

            const typeA = a.containerType?.name ?? "";
            const typeB = b.containerType?.name ?? "";

            return typeA.localeCompare(typeB);
          });

        setContainerData(sortedContainers);
      }
    }
  }, [
    vessels,
    selectedVessel,
    selectedVesselForPlanner,
    filteredTerminalCall,
    terminalCalls,
    planType,
    containers,
    shiftedContainers,
    mainVessels,
    dispatch,
  ]);

  const selectedVesselForPlannerHandler = (
    event: React.MouseEvent<HTMLElement>,
    vessel: Vessel
  ) => {
    setSelectedVesselForPlanner(vessel);
    dispatch(addVesselFilterValue(vessel));
  };

  const selectedTerminalCallHandler = (event: any) => {
    const element = event.target.closest(".js-terminal-call-parent");
    const terminalCallId = element?.getAttribute("data-terminal-call-id");

    if (!terminalCallId) {
      return;
    }

    const terminalCall = findObjectById(terminalCalls, terminalCallId);

    if (!terminalCall) {
      return;
    }

    setSearchTerm("");
    dispatch(clearContainerLabelAndTypeFilters());
    dispatch(addSelectedTerminalCallFilterValue(terminalCall));
    setDockingSide(terminalCall?.dockingSide ?? DOCKING_SIDE_PORT);
  };

  const hasPositionBarrier = (axis: any) => {
    const requirementsObtained: any[] = [];

    if (
      !selectedVessel ||
      !selectedVesselForPlanner ||
      selectedVesselForPlanner?.barriers?.length === 0
    ) {
      return false;
    }

    selectedVesselForPlanner?.barriers?.forEach((barrierPosition: any) => {
      requirementsObtained.push(
        JSON.stringify(barrierPosition) === JSON.stringify(axis)
      );
    });

    return requirementsObtained.includes(true);
  };

  const isDummyAt = useCallback(
    (x: number, y: number) => {
      const dummies = selectedVesselForPlanner?.dummies;

      if (!Array.isArray(dummies)) {
        return false;
      }

      return dummies.some(
        (pair: any) => Array.isArray(pair) && pair[0] === x && pair[1] === y
      );
    },
    [selectedVesselForPlanner]
  );

  const dragStart = (event: any) => {
    const target = event.currentTarget as HTMLElement;
    const id = target.getAttribute("data-container-id");
    const teu = target.getAttribute("data-container-teu");
    const x = target.getAttribute("data-grid-x");
    const y = target.getAttribute("data-grid-y");
    const onboard = target.getAttribute("data-grid-onboard");

    const containerObject = findObjectById(containerData, id);

    draggedContainer.current.id = id;
    draggedContainer.current.teu = teu;
    draggedContainer.current.x = x;
    draggedContainer.current.y = y;
    draggedContainer.current.onboard = onboard;
    draggedContainer.current.loadingTerminalId =
      containerObject?.loadingTerminal?.["@id"] ?? null;
  };

  const handleDragEnd = () => {
    setTerminalColumnDragOver(false);
    draggedContainer.current = {};
  };

  const dragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();

    const inLoadingRow = Boolean(
      (event.currentTarget as HTMLElement).closest(
        ".container-planner-loading-container-row"
      )
    );

    if (!event.currentTarget.hasAttribute("data-container-list") && !inLoadingRow) {
      event.currentTarget.classList.add("drag-over-grid");
    }
  };

  const addContainer = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      openModal({
        title: t("addFormTitleByType", { type: t("container") }),
        description: "Add a container",
        form: "container",
        formProps: {
          terminals: terminals.data,
          vessels: vessels.data,
          labels: labels.data,
          voyages: voyages.data,
          selectedVessel,
          selectedLoadingTerminalCall: filteredTerminalCall,
        },
      });
    },
    [
      terminals,
      vessels,
      labels,
      voyages,
      selectedVessel,
      filteredTerminalCall,
      openModal,
      t,
    ]
  );

  const addMultipleContainers = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      openModal({
        title: t("addFormTitleByType", { type: t("containers") }),
        description: "Add multple containers",
        form: "simplemultiplecontainer",
        formProps: {
          terminalCalls,
          selectedVessel,
          selectedLoadingTerminalCall: filteredTerminalCall,
          containerTypes: containerTypes.data,
        },
      });
    },
    [
      terminalCalls,
      filteredTerminalCall,
      selectedVessel,
      containerTypes,
      openModal,
      t,
    ]
  );

  const addTransferContainers = useCallback(
    (event: React.MouseEvent, containers: any) => {
      event.stopPropagation();
      console.log(containers);
    },
    []
  );

  const handleTerminalColumnDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setTerminalColumnDragOver(true);
  };

  const handleTerminalColumnDragLeave = (
    event: React.DragEvent<HTMLElement>
  ) => {
    const current = event.currentTarget as HTMLElement;
    const related = event.relatedTarget as Node | null;

    if (!related || !current.contains(related)) {
      setTerminalColumnDragOver(false);
    }
  };

  const refreshSelectedTerminalCalls = useCallback(async () => {
    if (!voyage?.terminalCalls) {
      return;
    }

    await dispatch(
      getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)) as any
    );
  }, [dispatch, voyage]);

  const refreshPlannerAfterContainerUpload = useCallback(
    async (result?: any) => {
      const refreshedVoyage = result?.voyage || voyage;

      if (refreshedVoyage) {
        await refreshVoyageData(refreshedVoyage);
        stayOnSelectedTerminalCall();
      }

      const terminalCallIds = normalizeIds(
        refreshedVoyage?.terminalCalls || voyage?.terminalCalls || []
      );

      if (terminalCallIds.length > 0) {
        await dispatch(getSelectedTerminalCalls(terminalCallIds) as any);
      }
    },
    [dispatch, refreshVoyageData, voyage, stayOnSelectedTerminalCall]
  );

  const updateContainerPosition = useCallback(
    (container: Container, position: number[] | null, vessel: Vessel) => {
      dispatch(
        updateSelectedContainer({
          ...container,
          position,
          vessel,
        })
      ).then(() => {
        startPositionStream(normalizeId((container as any)?.["@id"]));
        dispatch(getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)));
      });
    },
    [dispatch, voyage]
  );

  const shiftContainerPosition = useCallback(
    async (
      container: Container,
      position: number[] | null,
      vessel: Vessel,
      terminalCall: TerminalCall
    ) => {
      if (!position) {
        return;
      }

      try {
        const { ["@id"]: _omit, ...base } = container as any;

        const createContainerPayload: any = {
          ...base,
          position,
          loadingTerminal: (terminalCall as any).terminal,
          loadingTerminalCall: terminalCall,
          shiftedBackIn: true,
          shifterOriginal: (container as any)["@id"],
          vessel,
          shiftedDate: new Date().toISOString(),
          fileGroup: null,
        };

        dispatch(createSelectedContainer(createContainerPayload))
          .then((createdContainer: any) => {
            const shifterContainer = createdContainer["@id"];
            const containerId = normalizeId(shifterContainer);

            return dispatch(
              updateSelectedContainer({
                ...(container as any),
                unloadingTerminal: (terminalCall as any).terminal,
                unloadingTerminalCall: terminalCall,
                shiftedOut: true,
                vessel,
                shiftedDate: new Date().toISOString(),
                shifter: shifterContainer,
              })
            ).then(() => ({
              containerId,
            }));
          })
          .then(({ containerId }: any) => {
            startPositionStream(containerId);
            dispatch(getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)));
          })
          .catch((err: any) => {
            console.error("Error shifting container:", err);
          });
      } catch (err) {
        console.error("Error shifting container:", err);
      }
    },
    [dispatch, voyage]
  );

  const getContainerColorTypeByPlanType = useCallback((nextPlanType: string) => {
    return nextPlanType === PLAN_TYPE_UNLOADING
      ? LOADING_CONTAINER_COLOR
      : UNLOADING_CONTAINER_COLOR;
  }, []);

  const getPdfPlanTypeLabel = useCallback(
    (nextPlanType: string) => {
      switch (nextPlanType) {
        case PLAN_TYPE_LOADING_ONLY:
          return t("loading");

        case PLAN_TYPE_UNLOADING:
          return t("unloading");

        case PLAN_TYPE_LOADING:
          return t("stowagePlan");

        case PLAN_TYPE_SHIFTERS:
          return t("shifters");

        default:
          return t("planning");
      }
    },
    [t]
  );

  const showPdfButtonArrow = isMenuPdfPlanType(planType);

  const pdfButtonLabel = showPdfButtonArrow
    ? `${t("chooseOne")} PDF / Email`
    : `${getPdfPlanTypeLabel(planType)} PDF / Email`;

  const openPlanningPdfDialog = useCallback(
    (
      selectedPlanType: string = planType,
      selectedContainerColorType: string = containerColorType
    ) => {
      openDialog({
        title: `${getPdfPlanTypeLabel(selectedPlanType)} PDF / Email`,
        content: "terminalcallcontainerplanning",
        dialogProps: {
          containers: containerData,
          selectedTerminalCall: filteredTerminalCall,
          selectedVessel: selectedVesselForPlanner,
          containerColorType: selectedContainerColorType,
          dockingSide,
          planType: selectedPlanType,
          containerTypes,
        },
      });
    },
    [
      openDialog,
      getPdfPlanTypeLabel,
      planType,
      containerColorType,
      containerData,
      filteredTerminalCall,
      selectedVesselForPlanner,
      dockingSide,
      containerTypes,
    ]
  );

  const handlePdfButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!showPdfButtonArrow) {
      openPlanningPdfDialog(planType, containerColorType);
      return;
    }

    setPdfMenuAnchor(event.currentTarget);
  };

  const handlePdfMenuClose = () => {
    setPdfMenuAnchor(null);
  };

  const handlePdfPlanChoice = (nextPlanType: string) => {
    const nextContainerColorType = getContainerColorTypeByPlanType(nextPlanType);

    setPdfMenuAnchor(null);
    setPlanType(nextPlanType);
    setContainerColorType(nextContainerColorType);

    openPlanningPdfDialog(nextPlanType, nextContainerColorType);
  };

  async function startPositionStream(containerId: any) {
    setProcessing(true);
    setAutoPlacing(false);
    setProcessedCount(0);
    setTotalCount(0);

    const res = await fetch(`/api/container/${containerId}/grid/position`, {
      method: "POST",
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    const reader = res.body?.getReader();

    if (!reader) {
      setProcessing(false);
      return;
    }

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split("\n\n");
      buffer = parts.pop()!;

      for (const part of parts) {
        const lines = part.split("\n");
        const dataLine = lines.find((line) => line.startsWith("data:"));

        if (!dataLine) {
          continue;
        }

        const payload = JSON.parse(dataLine.replace(/^data:\s*/, ""));

        if (lines[0].startsWith("event: done")) {
          setTotalCount(payload.total);
          setProcessedCount(payload.total);
          setProcessing(false);
          dispatch(getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)));
        } else {
          setProcessedCount(payload.processed);
        }
      }
    }
  }

  const fixDuplicateTimings = useCallback(async () => {
    for (const group of duplicateGroups) {
      for (let i = 1; i < group.calls.length; i++) {
        const tc = group.calls[i];
        const currentDate = tc?.planned || tc?.arrival || tc?.operationStart;
        if (!currentDate) continue;
        const offset = timeOffsets[tc["@id"]] ?? i * 10;
        const newDate = dayjs(currentDate).add(offset, "minute").toISOString();
        const field = tc.planned ? "planned" : tc.arrival ? "arrival" : "operationStart";
        try {
          await fetch(`/api/terminal_calls/${normalizeId(tc["@id"])}`, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/merge-patch+json; charset=utf-8",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({ [field]: newDate }),
          });
        } catch (err) {
          console.error("Failed to fix duplicate timing for", tc["@id"], err);
        }
      }
    }
    setShowConfirmDialog(false);
    setSameTimeDialogOpen(false);
    window.location.reload();
  }, [duplicateGroups, timeOffsets]);

  const confirmStabilityReorganization = () =>
    new Promise<boolean>((resolve) => {
      stabilityReorgResolveRef.current = resolve;
      setShowStabilityReorgConfirm(true);
    });

  const handleStabilityReorgConfirm = (confirmed: boolean) => {
    stabilityReorgResolveRef.current?.(confirmed);
    stabilityReorgResolveRef.current = null;
    setShowStabilityReorgConfirm(false);
  };

  const containerChangeLoadingTerminalCallHandler = (event: any) => {
    event.preventDefault();

    if (!draggedContainer.current) {
      return;
    }

    const containerId = draggedContainer.current.id;
    const containerObject = findObjectById(containerData, containerId);

    if (!containerObject) {
      return;
    }

    const parent = event.target.closest(".js-terminal-call-parent");

    if (!parent) {
      return;
    }

    const terminalId = parent.getAttribute("data-terminal-id");
    const terminalCallId = parent.getAttribute("data-terminal-call-id");
    const containerTerminalId = containerObject?.loadingTerminal?.["@id"];
    const unloadingTerminalCallId =
      containerObject?.unloadingTerminalCall?.["@id"];

    if (
      (!containerObject && !terminalCallId) ||
      terminalId !== containerTerminalId ||
      terminalCallId === unloadingTerminalCallId ||
      !!containerObject?.position
    ) {
      return;
    }

    dispatch(
      updateSelectedContainer({
        ...containerObject,
        loadingTerminalCall: terminalCallId,
      })
    ).then(() => {
      startPositionStream(normalizeId(containerObject?.["@id"]));
      dispatch(getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)));
    });
  };

  const containerStateHandler = (event: any) => {
    event.preventDefault();

    if (!draggedContainer.current) {
      return;
    }

    let position = null;
    const hasBarrierOverflow: any[] = [];
    const containerId = draggedContainer.current.id;
    const containerTeu = parseInt(draggedContainer.current.teu, 10);
    const containerOnboard = draggedContainer.current.onboard === "true";
    const layer = event.target.getAttribute("data-grid-layer");
    const x = event.target.getAttribute("data-grid-x");
    const y = event.target.getAttribute("data-grid-y");

    let containerObject = findObjectById(containerData, containerId);

    if (!containerObject) {
      return;
    }

    if (containerTeu > 1) {
      [...Array((containerTeu === 4 ? 2 : containerTeu) - 1)].forEach(
        (_, gx) => {
          hasBarrierOverflow.push(
            hasPositionBarrier([
              parseInt(x, 10) + gx,
              parseInt(x, 10) + gx + 1,
            ])
          );
        }
      );
    }

    if (hasBarrierOverflow.includes(true)) {
      event.target.classList.remove("drag-over-grid");
      setSnackbar({
        children: "Overlapping a barrier",
        severity: "warning",
        variant: "filled",
      });
      return;
    }

    if (layer && x && y && selectedVesselForPlanner) {
      position = [
        parseInt(layer, 10),
        parseInt(x, 10),
        parseInt(y, 10),
        selectedVesselForPlanner["@id"].replace("/api/vessels/", ""),
      ];
    }

    if (!containerObject.vessel && filteredTerminalCall?.vessel) {
      containerObject = {
        ...containerObject,
        vessel: selectedVesselForPlanner,
      };
    }

    if (containerOnboard) {
      shiftContainerPosition(
        containerObject as any,
        position,
        selectedVesselForPlanner,
        filteredTerminalCall
      );
      return;
    }

    updateContainerPosition(
      containerObject as any,
      position,
      selectedVesselForPlanner
    );
  };

  const handleEmptyGridClick = (
    layer: number,
    xAxis: number,
    yAxis: number
  ) => {
    if (
      !selectedVesselForPlanner ||
      !filteredTerminalCall ||
      !firstContainerWithoutPosition
    ) {
      return;
    }

    const teu = firstContainerWithoutPosition.containerType?.teu ?? 1;
    const span = teu === 4 ? 2 : teu;

    for (let i = 0; i < span - 1; i++) {
      if (hasPositionBarrier([xAxis + i, xAxis + i + 1])) {
        setSnackbar({
          children: "Overlapping a barrier",
          severity: "warning",
          variant: "filled",
        });
        return;
      }
    }

    const vesselId = selectedVesselForPlanner["@id"].replace(
      "/api/vessels/",
      ""
    );
    const position = [layer, xAxis, yAxis, vesselId];

    updateContainerPosition(
      firstContainerWithoutPosition as any,
      position,
      selectedVesselForPlanner
    );
  };

  const isContainerFromArrivalContainers = (container: Container | undefined) => {
    if (container === undefined) {
      return false;
    }

    return (
      planType !== PLAN_TYPE_UNLOADING &&
      getTerminalCallArrivalStateContainerIds(filteredTerminalCall).includes(
        (container as any)["@id"]
      )
    );
  };

  const findContainerByPosition = (position: number[]) => {
    const posWithVessel = [...position];

    if (selectedVesselForPlanner) {
      posWithVessel.push(normalizeId(selectedVesselForPlanner["@id"]));
    }

    const found = containerData.find((container: any) => {
      const matchesPosition =
        container &&
        container.position &&
        JSON.stringify(container.position) === JSON.stringify(posWithVessel);

      if (!matchesPosition) {
        return false;
      }

      if (
        planType === PLAN_TYPE_LOADING &&
        container.unloadingTerminalCall &&
        container.unloadingTerminalCall["@id"] === filteredTerminalCall?.["@id"]
      ) {
        return false;
      }

      return true;
    });

    if (found) {
      return found;
    }

    const [layer, x, y] = position;

    if (layer === 1 && isDummyAt(x, y)) {
      return {
        dummy: true,
        uid: `DUMMY-${x}-${y}`,
        containerType: { teu: 1 },
        labels: [],
      };
    }

    return undefined;
  };

  const numberOfContainersWithPosition = () => {
    if (!containerData.length || !filteredTerminalCall) {
      return 0;
    }

    const plannedArray =
      planType === PLAN_TYPE_LOADING
        ? filteredTerminalCall.loadingContainers
        : filteredTerminalCall.unloadingContainers;

    const plannedIds = plannedArray.map((item: any) => item["@id"]);

    return containerData.filter(
      (container: any) =>
        container.position && plannedIds.includes(container["@id"])
    ).length;
  };

  const numberOfEmptyContainers = () => {
    if (!containerData.length || !filteredTerminalCall) {
      return 0;
    }

    const plannedArray =
      planType === PLAN_TYPE_LOADING
        ? filteredTerminalCall.loadingContainers
        : filteredTerminalCall.unloadingContainers;

    const plannedIds = plannedArray.map((item: any) => item["@id"]);

    return containerData.filter(
      (container: any) =>
        plannedIds.includes(container["@id"]) && container.empty
    ).length;
  };

  const updatedTerminalCall = useMemo(
    () => getUpdatedTerminalCall(),
    [terminalCalls, filteredTerminalCall]
  );

  const departureStabilityState = useMemo(
    () => getDepartureStabilityState(selectedVesselForPlanner, updatedTerminalCall),
    [selectedVesselForPlanner, updatedTerminalCall]
  );

  const containerPositionHandler = (
    layer: number,
    xAxis: number,
    yAxis: number,
    gridArray: number[],
    nextGridCountPerXAxis?: number
  ) => {
    const teu = parseInt(draggedContainer.current.teu ?? "1", 10) || 1;
    const containerXAxis = draggedContainer.current.x
      ? parseInt(draggedContainer.current.x, 10)
      : null;
    const containerYAxis = draggedContainer.current.y
      ? parseInt(draggedContainer.current.y, 10)
      : null;
    const totalCols = gridArray.length;
    const isBeforeLastRow = totalCols - 1 === xAxis;

    if (containerXAxis === xAxis && containerYAxis === yAxis) {
      return false;
    }

    if (teu === 1) {
      if (layer === 1) {
        return true;
      }

      const below = findContainerByPosition([layer - 1, xAxis, yAxis]);
      const leftBelow = findContainerByPosition([layer - 1, xAxis - 1, yAxis]);

      if (!below && leftBelow && (leftBelow as any)?.containerType.teu === 1) {
        return false;
      }

      if ((below as any)?.containerType.teu === 1) {
        return true;
      }

      if (
        (below as any)?.containerType.teu === 2 ||
        (leftBelow as any)?.containerType.teu === 2
      ) {
        return true;
      }

      return false;
    }

    if (teu === 2) {
      if (
        xAxis >= totalCols ||
        (nextGridCountPerXAxis &&
          nextGridCountPerXAxis !== 2 &&
          yAxis > nextGridCountPerXAxis)
      ) {
        return false;
      }

      if (findContainerByPosition([layer, xAxis + 1, yAxis])) {
        return false;
      }

      if (
        isBeforeLastRow &&
        nextGridCountPerXAxis === 2 &&
        (yAxis === 1 || yAxis === 4)
      ) {
        return false;
      }

      if (layer === 1) {
        return true;
      }

      const below = findContainerByPosition([layer - 1, xAxis, yAxis]);
      const belowRight = findContainerByPosition([layer - 1, xAxis + 1, yAxis]);

      if ((below as any)?.containerType.teu === 2) {
        return true;
      }

      if (
        (below as any)?.containerType.teu === 1 &&
        (belowRight as any)?.containerType.teu === 1
      ) {
        return true;
      }

      return false;
    }

    return false;
  };

  const dockingSideChange = (
    event: React.MouseEvent<HTMLElement>,
    nextDockingSide: string
  ) => {
    if (!nextDockingSide) {
      return;
    }

    setDockingSide(nextDockingSide);
  };

  const containerColorHandler = (container: Container | undefined) => {
    if (container === undefined) {
      return NO_CONTAINER_COLOR;
    }

    if (containerColorType === LOADING_CONTAINER_COLOR) {
      return (container as any).loadingTerminalCall &&
        (container as any).loadingTerminalCall.terminal
        ? (container as any).loadingTerminalCall.terminal.color
        : (container as any).loadingTerminal &&
          (container as any).loadingTerminal.color
        ? (container as any).loadingTerminal.color
        : DEFAULT_CONTAINER_COLOR;
    }

    if (containerColorType === UNLOADING_CONTAINER_COLOR) {
      const containerDataLocal: any = (container as any).shifter ?? container;

      return containerDataLocal.unloadingTerminalCall &&
        containerDataLocal.unloadingTerminalCall.terminal
        ? containerDataLocal.unloadingTerminalCall.terminal.color
        : containerDataLocal.unloadingTerminal &&
          containerDataLocal.unloadingTerminal.color
        ? containerDataLocal.unloadingTerminal.color
        : DEFAULT_CONTAINER_COLOR;
    }

    return DEFAULT_CONTAINER_COLOR;
  };

  const unpositionAllTerminalCallContainers = useCallback(async () => {
    if (!filteredTerminalCall) {
      return;
    }

    setIsUnpositioning(true);
    const startedAt = Date.now();
    const minDisplayMs = 1200;

    const waitForMinDisplay = async () => {
      const remaining = Math.max(0, minDisplayMs - (Date.now() - startedAt));
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
    };

    try {
      const res = await fetch(
        `/api/terminal_call/${normalizeId(filteredTerminalCall["@id"])}/unposition`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/ld+json; charset=utf-8",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!res.ok) {
        throw new Error(res.statusText);
      }

      await waitForMinDisplay();

      setSnackbar({
        children: "All containers unpositioned.",
        severity: "success",
        variant: "filled",
      });
    } catch (err: any) {
      await waitForMinDisplay();

      setSnackbar({
        children: `Failed to unposition: ${err.message}`,
        severity: "error",
        variant: "filled",
      });
    } finally {
      setIsUnpositioning(false);
      // Unpositioning clears positions server-side but leaves the container
      // query unchanged, so the cached selected-containers fetch would be
      // skipped. Force a full reload so the vessel grid reflects the new state.
      await refreshVoyageData(voyage, { force: true });
      stayOnSelectedTerminalCall();
    }
  }, [filteredTerminalCall, refreshVoyageData, voyage, stayOnSelectedTerminalCall]);

  const groupedUnpositionedContainers = useMemo(() => {
    if (!filteredTerminalCall || !containerData) {
      return {};
    }

    return containerData
      .filter(
        (container: any) =>
          !container.position &&
          container.loadingTerminalCall?.["@id"] === filteredTerminalCall["@id"] &&
          (filterLabels.length === 0 ||
            container.labels?.some((label: any) =>
              filterLabels.includes(label["@id"])
            )) &&
          (filterTypes.length === 0 ||
            filterTypes.includes(container.containerType?.["@id"])) &&
          (searchTerm.length < 3 ||
            (() => {
              const term = searchTerm.toLowerCase();
              const { letters, numbers } = containerUidFormatter(container.uid);

              return (
                letters.toLowerCase().includes(term) ||
                numbers.toLowerCase().includes(term) ||
                container.uid.toLowerCase().includes(term)
              );
            })())
      )
      .reduce((acc: Record<string, Container[]>, container: any) => {
        const terminalName =
          container.unloadingTerminalCall?.terminal.name || "Unknown";

        if (!acc[terminalName]) {
          acc[terminalName] = [];
        }

        acc[terminalName].push(container);

        return acc;
      }, {});
  }, [
    containerData,
    filteredTerminalCall,
    searchTerm,
    filterLabels,
    filterTypes,
  ]);

  const firstContainerWithoutPosition = useMemo(() => {
    if (!filteredTerminalCall) {
      return undefined;
    }

    const allUnpositioned = (
      Object.values(groupedUnpositionedContainers) as Container[][]
    ).flat();

    return allUnpositioned.length > 0 ? allUnpositioned[0] : undefined;
  }, [groupedUnpositionedContainers, filteredTerminalCall]);

  const hasUnpositionedContainers = useMemo(() => {
    if (!filteredTerminalCall) {
      return false;
    }

    return containerData.some(
      (container: any) =>
        !container.position &&
        container.loadingTerminalCall?.["@id"] === filteredTerminalCall["@id"]
    );
  }, [containerData, filteredTerminalCall]);

  const unpositionedContainerCount = useMemo(() => {
    if (!filteredTerminalCall) {
      return 0;
    }

    return containerData.filter(
      (container: any) =>
        !container.position &&
        container.loadingTerminalCall?.["@id"] === filteredTerminalCall["@id"]
    ).length;
  }, [containerData, filteredTerminalCall]);

  const currentCallHasContainersToLoad = useMemo(() => {
    if (!filteredTerminalCall) {
      return false;
    }

    const loadingIds = getTerminalCallLoadingContainerIds(filteredTerminalCall);

    return Array.isArray(loadingIds) && loadingIds.length > 0;
  }, [filteredTerminalCall]);

  const previousTerminalCallForPlanner = useMemo(() => {
    if (!filteredTerminalCall) {
      return undefined;
    }

    const currentId = filteredTerminalCall["@id"];
    const currentTime =
      getTerminalCallDateTimeKey(filteredTerminalCall) ??
      Number.MAX_SAFE_INTEGER;

    return (
      Object.values(terminalCallData || {})
        .flat()
        .filter(
          (tc: any) =>
            tc["@id"] !== currentId &&
            (getTerminalCallDateTimeKey(tc) ?? 0) < currentTime
        )
        .sort(
          (a: any, b: any) =>
            (getTerminalCallDateTimeKey(b) ?? 0) -
            (getTerminalCallDateTimeKey(a) ?? 0)
        )[0] as any
    );
  }, [terminalCallData, filteredTerminalCall, getTerminalCallDateTimeKey]);

  const previousCallHasUnpositionedContainers = useMemo(() => {
    if (!previousTerminalCallForPlanner) {
      return false;
    }

    const prevId = previousTerminalCallForPlanner["@id"];

    return (containers.data || []).some(
      (container: any) =>
        container.loadingTerminalCall?.["@id"] === prevId &&
        (!Array.isArray(container.position) || container.position.length === 0)
    );
  }, [previousTerminalCallForPlanner, containers]);

  const nextTerminalCallForPlanner = useMemo(() => {
    if (!filteredTerminalCall) {
      return undefined;
    }

    const currentId = filteredTerminalCall["@id"];
    const currentTime =
      getTerminalCallDateTimeKey(filteredTerminalCall) ?? 0;

    return (
      Object.values(terminalCallData || {})
        .flat()
        .filter(
          (tc: any) =>
            tc["@id"] !== currentId &&
            (getTerminalCallDateTimeKey(tc) ?? Number.MAX_SAFE_INTEGER) >
              currentTime
        )
        .sort(
          (a: any, b: any) =>
            (getTerminalCallDateTimeKey(a) ?? 0) -
            (getTerminalCallDateTimeKey(b) ?? 0)
        )[0] as any
    );
  }, [terminalCallData, filteredTerminalCall, getTerminalCallDateTimeKey]);

  const nextCallHasPositionedContainers = useMemo(() => {
    if (!nextTerminalCallForPlanner) {
      return false;
    }

    const nextId = normalizeId(nextTerminalCallForPlanner["@id"]);

    return (containers.data || []).some(
      (container: any) =>
        normalizeId(container.loadingTerminalCall?.["@id"]) === nextId &&
        Array.isArray(container.position) &&
        container.position.length > 0
    );
  }, [nextTerminalCallForPlanner, containers]);

  const crossCallOverlaps = useMemo(() => {
    const prevId = normalizeId(previousTerminalCallForPlanner?.["@id"]);
    const nextId = normalizeId(nextTerminalCallForPlanner?.["@id"]);
    const currentId = normalizeId(filteredTerminalCall?.["@id"]);
    const vesselId = normalizeId(selectedVesselForPlanner?.["@id"]);
    const cellMap = new Map<string, any[]>();

    (containers.data || []).forEach((container: any) => {
      if (!Array.isArray(container.position) || container.position.length < 3) {
        return;
      }

      const cid = normalizeId(container.vessel?.["@id"]);
      if (vesselId && cid && cid !== vesselId) return;

      const loadingId = normalizeId(container.loadingTerminalCall?.["@id"]);
      const isToBeLoaded =
        loadingId === currentId || loadingId === prevId || loadingId === nextId;

      if (!isToBeLoaded) {
        return;
      }

      const [l, x, y] = container.position;
      const span =
        container.containerType?.teu === 4
          ? 2
          : Math.max(Number(container.containerType?.teu) || 1, 1);

      for (let i = 0; i < span; i++) {
        const key = `${l}-${x + i}-${y}`;
        if (!cellMap.has(key)) {
          cellMap.set(key, []);
        }
        cellMap.get(key).push(container);
      }
    });

    const groups = Array.from(cellMap.values()).filter(
      (containers) => containers.length > 1
    );

    const groupsInvolvingCurrentCall = groups.filter((group) =>
      group.some(
        (container: any) =>
          normalizeId(container.loadingTerminalCall?.["@id"]) === currentId
      )
    );

    const containersToUnposition: any[] = [];
    const seen = new Set<string>();

    groupsInvolvingCurrentCall.forEach((group) => {
      group.forEach((container: any) => {
        const id = normalizeId(container["@id"]);
        if (id && !seen.has(id)) {
          seen.add(id);
          containersToUnposition.push(container);
        }
      });
    });

    return {
      overlapCount: groupsInvolvingCurrentCall.length,
      containersToUnposition,
    };
  }, [
    containers.data,
    previousTerminalCallForPlanner,
    nextTerminalCallForPlanner,
    filteredTerminalCall,
    selectedVesselForPlanner,
  ]);

  const autoPlaceRemainingContainers = async (maximumLayers?: number) => {
    if (
      !selectedVesselForPlanner?.positions ||
      !filteredTerminalCall
    ) {
      return;
    }

    const positions = selectedVesselForPlanner.positions;
    const plannerSetting = normalizeVesselPlannerSetting(
      selectedVesselForPlanner.plannerSettings ??
        selectedVesselForPlanner.plannerSetting
    );
    setAppliedPlannerSettings({
      constraints: plannerSetting.constraints,
      groupingPreference: plannerSetting.groupingPreference,
      incompleteLayerPreference: plannerSetting.incompleteLayerPreference,
      exclusions: plannerSetting.excludedPositions.length,
      sequence: plannerSetting.stackingSequence.length,
    });
    setAutoPlacing(true);
    const stackingSequence = new Map(
      plannerSetting.stackingSequence.map(([x, y], index) => [
        `${x}-${y}`,
        index,
      ])
    );
    const spanOf = (container: any) => getContainerGridSpan(container);
    const cellKey = (l: number, x: number, y: number) => `${l}-${x}-${y}`;
    const stackKey = (x: number, y: number) => `${x}-${y}`;

    // Destination zone reservations: floor stacks are assigned to a destination
    // up front so findPosition can restrict its search to that zone, then spill
    // into unreserved stacks, and only as a last resort into another zone.
    // Filled in once the eligible container set is known (see below).
    const destinationReservations = new Map<string, Set<string>>();
    // 1-TEU (20ft) and 2-TEU (40ft) footprints are reserved separately so a
    // 20ft can never consume a footprint that was set aside for a 40ft (and
    // vice versa) - see reservationZoneCost.
    const destinationSingles = new Map<string, Set<string>>();
    const destinationPairs = new Map<string, Set<string>>();
    const reservedStackOwner = new Map<string, string>();
    const getDestinationSortKey = (container: any) =>
      normalizeId(container.unloadingTerminalCall?.["@id"]) ||
      container.unloadingTerminalCall?.terminal?.name ||
      container.unloadingTerminal?.name ||
      "__unknown__";
    const terminalCallOrder = new Map<string, number>();
    (terminalCalls || []).forEach((terminalCall: any) => {
      const id = normalizeId(terminalCall?.["@id"]);
      const timestamp = terminalCall?.planned
        ? new Date(terminalCall.planned).getTime()
        : Number.NaN;
      if (id && Number.isFinite(timestamp)) terminalCallOrder.set(id, timestamp);
    });
    const getDischargeOrder = (container: any): number | null => {
      const id = normalizeId(container?.unloadingTerminalCall?.["@id"]);
      if (id && terminalCallOrder.has(id)) return terminalCallOrder.get(id)!;
      const planned = container?.unloadingTerminalCall?.planned;
      const timestamp = planned ? new Date(planned).getTime() : Number.NaN;
      return Number.isFinite(timestamp) ? timestamp : null;
    };

    const vesselId = normalizeId(selectedVesselForPlanner["@id"]);
    const occupied = new Set<string>();
    const containerAtCell = new Map<string, any>();
    const stackCohorts = new Map<string, string>();

    // Incremental per-layer weight / height sums mirroring the backend
    // stability model, used to score candidates by projected vertical center
    // of gravity (KG) in O(layers) without cloning the whole stowage on every
    // candidate. Dummies are excluded (support cells are not real containers).
    const baseOffset = Number(selectedVesselForPlanner?.baseOffset ?? 0.5);
    const emptyWeightInTonnes =
      Number(selectedVesselForPlanner?.emptyWeightInTonnes ?? 1000) || 1000;
    const emptyKg =
      Number(selectedVesselForPlanner?.emptyCenterOfGravity?.z ?? 1) || 1;
    const layerWeightTonnes = new Array(positions.length + 1).fill(0);
    const layerHeightSum = new Array(positions.length + 1).fill(0);
    const layerHeightCount = new Array(positions.length + 1).fill(0);

    const addToLayerProfile = (container: any, layer: number) => {
      layerWeightTonnes[layer] += getContainerWeightInTonnes(container);
      layerHeightSum[layer] += Number(
        container?.containerType?.height ?? 2.89
      );
      layerHeightCount[layer] += 1;
    };

    const removeFromLayerProfile = (container: any, layer: number) => {
      layerWeightTonnes[layer] -= getContainerWeightInTonnes(container);
      layerHeightSum[layer] -= Number(
        container?.containerType?.height ?? 2.89
      );
      layerHeightCount[layer] -= 1;
    };

    // Current placement of every container that was placed by this auto-plan
    // run, so the stability correction pass can relocate them afterwards.
    const placedContainers = new Map<string, any>();
    const placementById = new Map<string, number[]>();

    // Running total container weight per bay (x = length) and per slot
    // (y = beam), in kg, kept in sync with the plan as it is built. Used to
    // tie-break between equally-prioritised stack candidates so the stowage
    // stays balanced over the beam and over the length.
    const columnWeights = new Array((positions[0]?.length ?? 0) + 1).fill(0);
    const maxSlots =
      (positions[0] ?? []).reduce(
        (max, value) => Math.max(max, Number(value) || 0),
        0
      ) || 0;
    const slotWeights = new Array(maxSlots + 1).fill(0);

    // Weight per beam slot tracked per destination group, so the planner can
    // keep every group symmetric over the beam (port vs starboard) instead of
    // only balancing the total, which can hide a group leaning to one side.
    const groupSlotWeights = new Map<string, number[]>();
    const addGroupSlotWeight = (
      destination: string,
      y: number,
      weightKg: number
    ) => {
      const groupSlots =
        groupSlotWeights.get(destination) ??
        new Array(maxSlots + 1).fill(0);
      groupSlots[y] += weightKg;
      groupSlotWeights.set(destination, groupSlots);
    };
    const removeGroupSlotWeight = (
      destination: string,
      y: number,
      weightKg: number
    ) => {
      const groupSlots = groupSlotWeights.get(destination);
      if (!groupSlots) return;
      groupSlots[y] -= weightKg;
    };

    // A 40-foot (2-TEU) container spans two longitudinal bays, so its weight is
    // distributed across both bays for the length-balance projection. The beam
    // (slot) weight stays concentrated on the single slot the footprint sits in.
    const addColumnWeight = (x: number, span: number, weightKg: number) => {
      const perBay = weightKg / Math.max(1, span);
      for (let i = 0; i < span; i += 1) {
        columnWeights[x + i] += perBay;
      }
    };
    const removeColumnWeight = (x: number, span: number, weightKg: number) => {
      const perBay = weightKg / Math.max(1, span);
      for (let i = 0; i < span; i += 1) {
        columnWeights[x + i] -= perBay;
      }
    };

    // Pre-positioned containers already sitting in the plan. Stacks that turn
    // out to be unsupported by the tier below are removed again below and
    // routed back through findPosition, so keep the ones belonging to this in
    // a dedicated working list.
    const positioned: any[] = [];

    containerData.forEach((container: any) => {
      if (!container.position) return;
      if (
        normalizeId(container.unloadingTerminalCall?.["@id"]) ===
        normalizeId(filteredTerminalCall["@id"])
      ) {
        return;
      }
      const cid = normalizeId(container.vessel?.["@id"]);
      if (vesselId && cid && cid !== vesselId) return;
      positioned.push(container);
      const [l, px, py] = container.position;
      const span = spanOf(container);
      const destination = getDestinationSortKey(container);
      addColumnWeight(px, span, Number(container.weight) || 0);
      slotWeights[py] += Number(container.weight) || 0;
      addGroupSlotWeight(destination, py, Number(container.weight) || 0);
      addToLayerProfile(container, l);
      for (let i = 0; i < span; i++) {
        occupied.add(cellKey(l, px + i, py));
        containerAtCell.set(cellKey(l, px + i, py), container);
        const footprintKey = stackKey(px + i, py);
        const existingCohort = stackCohorts.get(footprintKey);
        stackCohorts.set(
          footprintKey,
          existingCohort && existingCohort !== destination
            ? "__mixed__"
            : destination
        );
      }
    });

    const nextCallRef = nextTerminalCallForPlanner?.["@id"];
    const nextCallId = normalizeId(nextCallRef);
    if (nextCallId) {
      (containers.data || []).forEach((container: any) => {
        const containerLoadingCallId = normalizeId(
          container.loadingTerminalCall?.["@id"]
        );
        if (!container.position || containerLoadingCallId !== nextCallId) {
          return;
        }
        const cid = normalizeId(container.vessel?.["@id"]);
        if (vesselId && cid && cid !== vesselId) return;
        const [l, px, py] = container.position;
        const span = spanOf(container);
        const destination = getDestinationSortKey(container);
        addColumnWeight(px, span, Number(container.weight) || 0);
        slotWeights[py] += Number(container.weight) || 0;
        addGroupSlotWeight(destination, py, Number(container.weight) || 0);
        for (let i = 0; i < span; i++) {
          occupied.add(cellKey(l, px + i, py));
          containerAtCell.set(cellKey(l, px + i, py), container);
          const footprintKey = stackKey(px + i, py);
          const existingCohort = stackCohorts.get(footprintKey);
          stackCohorts.set(
            footprintKey,
            existingCohort && existingCohort !== destination
              ? "__mixed__"
              : destination
          );
        }
      });
    }

    (selectedVesselForPlanner.dummies ?? []).forEach(
      ([dx, dy]: [number, number]) => {
        const dummyKey = cellKey(1, dx, dy);
        occupied.add(dummyKey);
        // Dummy cells occupy a full 1-TEU grid slot and can carry a stack,
        // so register them as a support container (see teu-stack-compatibility).
        containerAtCell.set(dummyKey, {
          "@id": `auto-planner-dummy-${dx}-${dy}`,
          uid: "",
          dummy: true,
          containerType: { teu: 1 },
        });
      }
    );

    // Exclusions are stored as floor footprints and block the complete stack.
    // Absent planner settings normalize to the default (no exclusions).
    plannerSetting.excludedPositions.forEach(([x, y]) => {
      for (let layer = 1; layer <= positions.length; layer += 1) {
        occupied.add(cellKey(layer, x, y));
      }
      stackCohorts.set(stackKey(x, y), "__blocked__");
    });

    const free = (l: number, x: number, y: number) =>
      !occupied.has(cellKey(l, x, y));
    const slotMaxAt = (l: number, x: number) => positions[l - 1]?.[x - 1] ?? 0;

    // Floor cells are reserved per destination as ONE contiguous centered block.
    // Within each bay the port and starboard slots alternate.
    const floorColumns = positions[0]?.length ?? 0;
    const slotAlternation = (slots: number[]) => {
      const result: number[] = [];
      for (
        let port = 0, starboard = slots.length - 1;
        port <= starboard;
        port += 1, starboard -= 1
      ) {
        if (port === starboard) result.push(slots[port]);
        else {
          result.push(slots[port]);
          result.push(slots[starboard]);
        }
      }
      return result;
    };
    const reservationWalkRank = new Map<string, number>();

    // Prefer the user stacking sequence when one is configured; otherwise follow
    // the reservation walk so the zone fills in the reserved block order.
    const sequenceCostOf = (x: number, y: number) => {
      if (stackingSequence.size > 0) {
        return stackingSequence.get(stackKey(x, y)) ?? stackingSequence.size;
      }
      return reservationWalkRank.get(stackKey(x, y)) ?? Number.MAX_SAFE_INTEGER;
    };

    const axisDiffKg = (values: number[]) => {
      const n = values.length;
      if (n === 0) return 0;
      const middleIndex = Math.floor(n / 2);
      let left = 0;
      let right = 0;
      values.forEach((value, index) => {
        if (n % 2 === 1) {
          if (index < middleIndex) left += value;
          else if (index > middleIndex) right += value;
          else {
            left += value / 2;
            right += value / 2;
          }
        } else {
          // An even slot count has no center slot: the port/starboard split
          // runs between the two middle slots (half of the slots each), which
          // is the same split shown in the vessel's "Weight over beam" view.
          if (index < middleIndex) left += value;
          else right += value;
        }
      });
      return Math.abs(left - right);
    };
    const columnMomentKg = (values: number[]) => {
      // Moment of the load about the vessel's longitudinal centre in kg*bay.
      // Lower means the weight sits closer to amidships, so the heaviest
      // containers (placed first) settle in the middle bays and the
      // weight-over-length indicator stays centred instead of spreading the
      // load equally across front, middle and back.
      const center = values.length / 2;
      let moment = 0;
      values.forEach((weight, index) => {
        moment += weight * Math.abs(index + 1 - center);
      });
      return moment;
    };
    const projectedImbalance = (
      x: number,
      y: number,
      span: number,
      weight: any,
      destination: string
    ): { beam: number; length: number } => {
      const weightKg = Number(weight) || 0;
      const projectedColumns = columnWeights.slice();
      const projectedSlots = slotWeights.slice();
      const perBay = weightKg / Math.max(1, span);
      for (let i = 0; i < span; i += 1) {
        projectedColumns[x + i] += perBay;
      }
      projectedSlots[y] += weightKg;
      // Beam: keep port and starboard level, and keep each destination group
      // symmetric too (a group can lean to one side even when the total beam
      // is balanced).
      let beam = 0;
      if (plannerSetting.constraints.balanceOverBeam) {
        beam = axisDiffKg(projectedSlots.slice(1));
        const groupSlots = groupSlotWeights.get(destination);
        if (groupSlots) {
          const projectedGroup = groupSlots.slice();
          projectedGroup[y] += weightKg;
          beam += axisDiffKg(projectedGroup.slice(1));
        }
      }
      const length = plannerSetting.constraints.balanceOverLength
        ? columnMomentKg(projectedColumns.slice(1))
        : 0;
      return { beam, length };
    };

    const groupingCost = (x: number, y: number, destination: string) => {
      const centerX = ((positions[0]?.length ?? 1) + 1) / 2;
      const centerY = (maxSlots + 1) / 2;

      const sameDestinationPositions = Array.from(stackCohorts.entries())
        .filter(([, cohort]) => cohort === destination)
        .map(([key]) => key.split("-").map(Number));
      const sameDestinationInBeam = Array.from(
        { length: maxSlots },
        (_, index) => stackCohorts.get(stackKey(x, index + 1))
      ).filter((cohort) => cohort === destination).length;

      if (plannerSetting.groupingPreference === "smallPiles") {
        return sameDestinationInBeam;
      }

      if (
        plannerSetting.groupingPreference === "centerBlocks" &&
        sameDestinationPositions.length > 0
      ) {
        return Math.min(
          ...sameDestinationPositions.map(
            ([destinationX, destinationY]) =>
              Math.abs(x - destinationX) + Math.abs(y - destinationY)
          )
        );
      }

      if (plannerSetting.groupingPreference === "centerBlocks") {
        return Math.abs(x - centerX) + Math.abs(y - centerY);
      }

      return -sameDestinationInBeam;
    };

    const incompleteLayerCost = (y: number) => {
      const distanceFromMiddle = Math.abs(y - (maxSlots + 1) / 2);
      return plannerSetting.incompleteLayerPreference === "middleRowsFirst"
        ? distanceFromMiddle
        : -distanceFromMiddle;
    };

    // How many 2-TEU footprints this placement destroys in its own row. A 40ft
    // needs two adjacent free cells at the same slot (y), so placing a 20ft in
    // the middle of a free run splits it into two odd fragments that can no
    // longer host a 40ft. Preferring the lowest loss keeps contiguous runs
    // intact for the larger spans still waiting to be placed, so every
    // container fits while the stowage still fills bottom-up.
    const capacityCost = (l: number, x: number, y: number, span: number) => {
      const usableAt = (bayX: number) =>
        bayX >= 1 &&
        bayX <= (positions[l - 1]?.length ?? 0) &&
        getUsableVesselSlots(slotMaxAt(l, bayX)).includes(y);
      if (!usableAt(x)) return 0;
      let runStart = x;
      while (usableAt(runStart - 1) && free(l, runStart - 1, y)) runStart -= 1;
      let runEnd = x + span - 1;
      while (usableAt(runEnd + 1) && free(l, runEnd + 1, y)) runEnd += 1;
      const left = x - runStart;
      const right = runEnd - (x + span - 1);
      const before = Math.floor((runEnd - runStart + 1) / 2);
      const after = Math.floor(left / 2) + Math.floor(right / 2);
      return before - after;
    };

    const projectedKg = (layer: number, container: any): number => {
      // Exact projection of the loaded vertical center of gravity in O(layers):
      // the candidate container is virtually added to its target layer while
      // the per-layer weight / height sums stay untouched.
      const addedWeight = getContainerWeightInTonnes(container);
      const addedHeight = Number(container?.containerType?.height ?? 2.89);
      const isAddingHere = (candidateLayer: number) =>
        candidateLayer === layer;

      let totalWeight = emptyWeightInTonnes;
      let totalMoment = emptyWeightInTonnes * emptyKg;
      let cumulativeHeight = baseOffset;

      for (let candidateLayer = 1; candidateLayer <= positions.length; candidateLayer += 1) {
        const count =
          layerHeightCount[candidateLayer] +
          (isAddingHere(candidateLayer) ? 1 : 0);
        const heightSum =
          layerHeightSum[candidateLayer] +
          (isAddingHere(candidateLayer) ? addedHeight : 0);
        const averageHeight = count > 0 ? heightSum / count : null;
        if (averageHeight === null || averageHeight <= 0) continue;

        const weight =
          layerWeightTonnes[candidateLayer] +
          (isAddingHere(candidateLayer) ? addedWeight : 0);
        totalWeight += weight;
        totalMoment += weight * (cumulativeHeight + averageHeight / 2);
        cumulativeHeight += averageHeight;
      }

      return totalWeight > 0 ? totalMoment / totalWeight : emptyKg;
    };

    const findPosition = (
      container: any,
      options?: {
        maximumLayer?: number;
        preferLowestLayer?: boolean;
        ignoreReservations?: boolean;
      }
    ): number[] | null => {
      const preferLowestLayer = Boolean(options?.preferLowestLayer);
      const ignoreReservations = Boolean(options?.ignoreReservations);
      const span = spanOf(container);
      const destination = getDestinationSortKey(container);

      // Prefer the destination's own reserved zone (0), then unreserved stacks
      // that no destination claims (1), and only as a last resort encroach into
      // another destination's zone (2). Without a reservation every footprint is
      // treated as in-zone and the planner behaves exactly as before. The
      // completion pass ignores reservations entirely so leftover containers
      // still find a spot anywhere on the vessel.
      const reservationZoneCost = (
        x: number,
        y: number,
        footprintSpan: number,
        target: string
      ) => {
        if (ignoreReservations) return 0;
        // 40ft containers may only score as "in-zone" on footprints reserved
        // for 40ft pairs, and 20ft containers only on footprints reserved for
        // 20ft singles. If a destination's single and pair footprints lived in
        // one set, the 20ft cargo placed first would consume the paired
        // footprints and strand the 40ft cargo that depends on them.
        const reservedCells =
          footprintSpan >= 2
            ? destinationPairs.get(target)
            : destinationSingles.get(target);
        if (!reservedCells) return 0;
        let insideZone = true;
        let unclaimed = true;
        for (let offset = 0; offset < footprintSpan; offset += 1) {
          const key = stackKey(x + offset, y);
          if (!reservedCells.has(key)) insideZone = false;
          if (reservedStackOwner.has(key)) unclaimed = false;
        }
        if (insideZone) return 0;
        if (unclaimed) return 1;
        return 2;
      };
      const candidates: Array<{
        position: number[];
        priority: number;
        layer: number;
        capacityCost: number;
        sequenceCost: number;
        groupingCost: number;
        incompleteLayerCost: number;
        stabilityCost: number;
        beamImbalance: number;
        lengthMoment: number;
        zoneCost: number;
      }> = [];
      // Apply the stack-compatibility rules in priority order and fall back
      // to the same-TEU stacking rule when the more specific rules do not
      // match. A 2-TEU bridge over two 1-TEU supports wins first; same-size
      // stacking is the fallback; a ground slot is only a last resort.
      const supportPriority = {
        "two-1-teu-supports": 0,
        "same-teu": 1,
        floor: 2,
      } as const;

      const layerLimit = Math.min(
        Math.max(
          1,
          options?.maximumLayer ??
            maximumLayers ??
            positions.length
        ),
        positions.length
      );

      const floorColumns = positions[0]?.length ?? 0;

      // Scan a footprint before moving to the next one. This builds stacks up
      // to the selected height and makes the TEU / layers estimate meaningful.
      for (let x = 1; x <= floorColumns - span + 1; x++) {
        let blocked = false;
        for (let i = 0; i < span - 1; i++) {
          if (hasPositionBarrier([x + i, x + i + 1])) {
            blocked = true;
            break;
          }
        }
        if (blocked) continue;

        const floorSlots = getUsableVesselSlots(slotMaxAt(1, x)).filter((slot) =>
          Array.from({ length: span }, (_, offset) =>
            getUsableVesselSlots(slotMaxAt(1, x + offset)).includes(slot)
          ).every(Boolean)
        );

        for (const y of floorSlots) {
          for (let l = 1; l <= layerLimit; l++) {
            const layerColumns = positions[l - 1]?.length ?? 0;
            if (x + span - 1 > layerColumns) continue;

            // A 20ft sitting in a bridge base (a cell that is both a reserved
            // single and the destination's own 40ft pair) must stay on layer 1,
            // so the 2-TEU container of the same destination can bridge over it
            // on layer 2 instead of being blocked by a tall 20ft stack.
            if (span === 1 && l > 1) {
              const baseKey = stackKey(x, y);
              if (
                destinationSingles.get(destination)?.has(baseKey) &&
                destinationPairs.get(destination)?.has(baseKey)
              ) {
                continue;
              }
            }

            const fitsLayerGrid = Array.from(
              { length: span },
              (_, offset) =>
                getUsableVesselSlots(slotMaxAt(l, x + offset)).includes(y)
            ).every(Boolean);
            if (!fitsLayerGrid) continue;

            const cellsAreFree = Array.from(
              { length: span },
              (_, offset) => free(l, x + offset, y)
            ).every(Boolean);
            if (!cellsAreFree) continue;

            // Only the cells directly beneath the footprint are real supports
            // for the candidate. getSupportingContainerLayouts also returns a
            // y-offset layout for legacy vessels, but that orientation can line
            // up containers that are NOT below the footprint and let a 2-TEU
            // container appear to rest on a single 1-TEU support while its
            // other cell floats. Evaluate the stack rules on the actual
            // beneath-footprint layout only.
            const supportLayouts = getSupportingContainerLayouts({
              layer: l,
              x,
              y,
              span,
              findContainer: (supportLayer, supportX, supportY) =>
                containerAtCell.get(
                  cellKey(supportLayer, supportX, supportY)
                ),
            });
            const supportingContainers = supportLayouts[0] ?? [];

            // Hard rule: when avoidShifters is selected a stack may only
            // contain containers of the same destination (colour). Floor
            // placement (layer 1) has no supports, and dummy support cells are
            // infrastructure (blocked floor slots that carry a stack), not
            // cargo, so they are exempt from the destination check. Without
            // avoidShifters different colours may share a stack as long as the
            // discharge order and TEU stack rules below still hold.
            const respectsSameDestination =
              !plannerSetting.constraints.avoidShifters ||
              l === 1 ||
              supportingContainers
                .filter((support) => support && !support.dummy)
                .every(
                  (support) =>
                    getDestinationSortKey(support) === destination
                );
            if (!respectsSameDestination) continue;

            const respectsStackRules = evaluateAutoPlannerRules({
              container,
              layer: l,
              x,
              y,
              span,
              supportingContainers,
            }).allowed;
            const respectsDischargeOrder =
              !plannerSetting.constraints.avoidShifters ||
              avoidsBlockingEarlierDischarge({
                container,
                supportingContainers,
                getDischargeOrder,
              });
            const supportKind = getTeuStackSupportKind({
              container,
              layer: l,
              x,
              y,
              span,
              supportingContainers,
            });
            const compatibleSupportKinds: Array<
              keyof typeof supportPriority
            > = [];
            if (
              respectsStackRules &&
              respectsDischargeOrder &&
              supportKind
            ) {
              compatibleSupportKinds.push(supportKind);
            }
            if (compatibleSupportKinds.length === 0) continue;

            if (plannerSetting.constraints.segregateAdn) {
              const neighboringContainers = Array.from(
                { length: span },
                (_, offset) => x + offset
              ).flatMap((neighborX) => [
                containerAtCell.get(cellKey(l, neighborX - 1, y)),
                containerAtCell.get(cellKey(l, neighborX + 1, y)),
                containerAtCell.get(cellKey(l, neighborX, y - 1)),
                containerAtCell.get(cellKey(l, neighborX, y + 1)),
                l > 1
                  ? containerAtCell.get(cellKey(l - 1, neighborX, y))
                  : undefined,
              ]);
              if (!respectsAdnSegregation({ container, neighboringContainers })) {
                continue;
              }
            }

                const projected = projectedImbalance(
                  x,
                  y,
                  span,
                  container.weight,
                  destination
                );
              candidates.push({
                position: [l, x, y],
                layer: l,
                capacityCost: capacityCost(l, x, y, span),
                priority: Math.min(
                  ...compatibleSupportKinds.map((kind) => supportPriority[kind])
                ),
                sequenceCost: sequenceCostOf(x, y),
                groupingCost: groupingCost(x, y, destination),
                incompleteLayerCost: incompleteLayerCost(y),
                stabilityCost: preferLowestLayer
                  ? 0
                  : projectedKg(l, container),
                beamImbalance: projected.beam,
                lengthMoment: projected.length,
                zoneCost: reservationZoneCost(x, y, span, destination),
              });
          }
        }
      }

      candidates.sort((left, right) => {
        const zoneComparison = left.zoneCost - right.zoneCost;
        if (zoneComparison !== 0) return zoneComparison;
        // Keep contiguous free runs intact first. A 40ft needs two adjacent
        // free cells, so a 20ft should never split a pair while a higher slot
        // is available; otherwise the larger spans still waiting are stranded
        // and not every container can be loaded on the free floor.
        const capacityComparison = left.capacityCost - right.capacityCost;
        if (capacityComparison !== 0) return capacityComparison;
        // Keep port/starboard level first, then pull the weight amidships. Both
        // rank before layer filling so the heaviest containers (placed first)
        // settle closest to the vessel centreline instead of leaning the ship
        // to port, starboard, bow or stern.
        const beamComparison = left.beamImbalance - right.beamImbalance;
        if (beamComparison !== 0) return beamComparison;
        const lengthComparison = left.lengthMoment - right.lengthMoment;
        if (lengthComparison !== 0) return lengthComparison;
        // Fill the current layer across every footprint before stacking any
        // container on a higher layer. Containers can only go up once the lower
        // layers are full (or blocked), so the stowage stays as low as possible.
        const layerComparison = left.layer - right.layer;
        if (layerComparison !== 0) return layerComparison;
        // Within the same layer, footprints are visited in stacking-sequence
        // order so each column still follows the configured sequence.
        const sequenceComparison = left.sequenceCost - right.sequenceCost;
        if (sequenceComparison !== 0) return sequenceComparison;
        const groupingComparison = left.groupingCost - right.groupingCost;
        if (groupingComparison !== 0) return groupingComparison;
        const incompleteLayerComparison =
          left.incompleteLayerCost - right.incompleteLayerCost;
        if (incompleteLayerComparison !== 0) return incompleteLayerComparison;
        // When restoring stability pick the lowest legal layer first; during
        // the initial placement prefer the candidate with the lowest projected
        // vertical center of gravity to keep heavy containers low.
        const verticalComparison =
          (preferLowestLayer ? left.layer : left.stabilityCost) -
          (preferLowestLayer ? right.layer : right.stabilityCost);
        if (verticalComparison !== 0) return verticalComparison;
        return left.priority - right.priority;
      });
      return candidates[0]?.position ?? null;
    };

    // Legacy plans can already contain physically impossible stacks: e.g. a
    // 2-TEU container whose supporting tier below is empty (the reported
    // "40ft over an empty layer" case). Such containers were loaded above only
    // so the balances/occupancy are computed; every container with a missing
    // support cell, plus everything stacked on top of it, is now pulled back
    // and re-placed through findPosition so the run never leaves illegal
    // stowage behind.
    const repairable = new Map<string, boolean>();
    for (const container of positioned) {
      const [l, px, py] = container.position;
      const span = spanOf(container);
      if (l <= 1) continue;
      for (let offset = 0; offset < span; offset++) {
        if (!containerAtCell.has(cellKey(l - 1, px + offset, py))) {
          repairable.set(normalizeId(container["@id"]), true);
          break;
        }
      }
    }
    let propagated = true;
    while (propagated) {
      propagated = false;
      for (const container of positioned) {
        const id = normalizeId(container["@id"]);
        if (repairable.has(id)) continue;
        const [l, px, py] = container.position;
        const span = spanOf(container);
        for (let offset = 0; offset < span; offset++) {
          const support = containerAtCell.get(
            cellKey(l - 1, px + offset, py)
          );
          if (
            support &&
            support !== container &&
            repairable.has(normalizeId(support["@id"]))
          ) {
            repairable.set(id, true);
            propagated = true;
            break;
          }
        }
      }
    }

    const repairQueue: any[] = [];
    for (const container of positioned) {
      if (!repairable.has(normalizeId(container["@id"]))) continue;
      const [l, px, py] = container.position;
      const span = spanOf(container);
      for (let offset = 0; offset < span; offset++) {
        occupied.delete(cellKey(l, px + offset, py));
        containerAtCell.delete(cellKey(l, px + offset, py));
        stackCohorts.delete(stackKey(px + offset, py));
      }
      removeFromLayerProfile(container, l);
      removeColumnWeight(px, span, Number(container.weight) || 0);
      slotWeights[py] -= Number(container.weight) || 0;
      removeGroupSlotWeight(
        getDestinationSortKey(container),
        py,
        Number(container.weight) || 0
      );
      repairQueue.push(container);
    }

    const eligible = containerData
      .filter(
        (container: any) =>
          !container.position &&
          container.loadingTerminalCall?.["@id"] === filteredTerminalCall["@id"]
      )
      .sort((left: any, right: any) => {
        // Place the 1-TEU (20ft) containers first so they claim their reserved
        // single footprints as floor bases; a 2-TEU (40ft) container can then
        // either bridge over a pair of same-destination 20ft bases or stack on
        // a 40ft of its own destination. Within the same size, the heaviest
        // container of that type always goes on the first (lowest) layer, the
        // next heaviest on the layer above it, and so on.
        const spanComparison = spanOf(left) - spanOf(right);
        if (spanComparison !== 0) return spanComparison;

        const weightComparison =
          getContainerWeightInTonnes(right) - getContainerWeightInTonnes(left);
        if (weightComparison !== 0) return weightComparison;

        if (plannerSetting.constraints.avoidShifters) {
          const leftOrder = getDischargeOrder(left);
          const rightOrder = getDischargeOrder(right);
          if (leftOrder !== null && rightOrder !== null && leftOrder !== rightOrder) {
            return rightOrder - leftOrder;
          }
        }

        const destinationComparison = getDestinationSortKey(left).localeCompare(
          getDestinationSortKey(right)
        );
        if (destinationComparison !== 0) return destinationComparison;

        return 0;
      });

    // Re-place the floating legacy stacks first, before the remaining
    // un-positioned containers compete for the same slots.
    eligible.unshift(...repairQueue);

    const totalUnpositioned = eligible.length;

    // Reserve floor stacks per destination before placement starts. Every
    // destination gets floor footprints sized from its requirement (one paired
    // 2-TEU footprint per 40-footer, one 1-TEU cell per 20-footer). The single
    // 1-TEU bases of EVERY destination are claimed before any paired 2-TEU
    // footprint, walking the vessel from the barrier bays outward, so each
    // destination is guaranteed floor space for its 20ft stacks no matter how
    // the larger destinations claim the vessel. Without this the biggest
    // destination swallows the whole floor and the last destination's 20ft
    // cargo (or its 40ft pair) is left behind.
    {
      const slotsByBay = new Map<number, number[]>();
      for (let x = 1; x <= floorColumns; x += 1) {
        slotsByBay.set(x, getUsableVesselSlots(slotMaxAt(1, x)));
      }

      const reservationNeeds = new Map<string, { twentyFt: number; fortyFt: number; weightKg: number }>();
      eligible.forEach((container: any) => {
        const dest = getDestinationSortKey(container);
        const need = reservationNeeds.get(dest) || { twentyFt: 0, fortyFt: 0, weightKg: 0 };
        if (getContainerGridSpan(container) >= 2) need.fortyFt += 1;
        else need.twentyFt += 1;
        need.weightKg += Number(container.weight) || 0;
        reservationNeeds.set(dest, need);
      });

      const reserveTargets = Array.from(reservationNeeds.entries())
        .filter(([, need]) => need.twentyFt > 0 || need.fortyFt > 0)
        .sort((left, right) => {
          // The heaviest destination group claims the central zone first so the
          // vessel's weight settles amidships. A big but light group in the
          // middle would push the heavy tail groups to the ends and trim the
          // vessel fore or aft.
          return right[1].weightKg - left[1].weightKg;
        })
        .map(([dest, need]) => {
          // Reservations must account for stacking: containers pile up to the
          // selected layer limit on each footprint, so a destination only
          // needs enough floor cells for its tallest stack, not one cell per
          // container. Otherwise the biggest destination over-claims the free
          // floor and the remaining containers can never be loaded.
          const requirement = calculateCohortSpaceRequirement(
            need.twentyFt,
            need.fortyFt,
            maximumLayers ?? positions.length
          );
          // Round the pair footprint count up to an even number so every
          // destination claims an equal number of port and starboard pairs and
          // its reserved zone (and therefore the vessel's beam) stays
          // symmetric. An odd pair count forces a 2:1 port/star split that no
          // amount of placement balancing can undo.
          const balancedPairs =
            requirement.twoTeuFootprints > 0 &&
            requirement.twoTeuFootprints % 2 !== 0
              ? requirement.twoTeuFootprints + 1
              : requirement.twoTeuFootprints;
          // A pair footprint converted into a 2-TEU bridge base holds one fewer
          // 40-foot container (layer 1 carries the 20ft bases instead), so only
          // the capacity headroom may be bridged or the 40-foot cargo is
          // stranded and the completion pass has to climb beyond the selected
          // layer count.
          const layers = maximumLayers ?? positions.length;
          const bridgeBudget = Math.max(
            0,
            balancedPairs * layers - need.fortyFt
          );
          return {
            dest,
            pairTarget: balancedPairs,
            singleTarget: requirement.oneTeuFootprints,
            bridgeBudget,
          };
        });

      destinationReservations.clear();
      destinationSingles.clear();
      destinationPairs.clear();
      reservedStackOwner.clear();

      // Claim footprints from a walk, skipping cells that are not free or
      // already reserved. Pair claims take two adjacent bays (for 40-footers)
      // that no barrier splits.
      const claimCells = (
        walk: Array<[number, number]>,
        limit: number,
        pair: boolean,
        dest: string,
        cells: Set<string>
      ) => {
        let claimed = 0;
        for (let cursor = 0; cursor < walk.length && claimed < limit; cursor += 1) {
          const [x, y] = walk[cursor];
          const key = stackKey(x, y);
          if (!free(1, x, y)) continue;
          if (reservedStackOwner.has(key)) continue;
          if (pair) {
            const nextX = x + 1;
            const nextKey = stackKey(nextX, y);
            const canPair =
              nextX <= floorColumns &&
              !hasPositionBarrier([x, nextX]) &&
              free(1, nextX, y) &&
              !reservedStackOwner.has(nextKey) &&
              getUsableVesselSlots(slotMaxAt(1, nextX)).includes(y);
            if (!canPair) continue;
            cells.add(key);
            cells.add(nextKey);
            reservedStackOwner.set(key, dest);
            reservedStackOwner.set(nextKey, dest);
          } else {
            cells.add(key);
            reservedStackOwner.set(key, dest);
          }
          claimed += 1;
        }
      };

      // Walk order across the floor. When a stacking sequence is configured it
      // is honored first: cells are visited in the user's sequence order so the
      // destination zones follow the sequence. Otherwise the walk follows the
      // grouping preference: fullBeamStrips fills whole slot rows from the
      // vessel's middle outwards so each destination's reserved cells form a
      // strip across the beam, centerBlocks clusters the destination zones
      // around the vessel's middle, and smallPiles puts the 1-TEU bases on the
      // barrier bays (they cannot pair across the barrier) before the remaining
      // bays from the centre outward.
      const globalWalk: Array<[number, number]> = [];
      const slotsAt = (x: number) => slotsByBay.get(x) || [];
      const usableAt = (x: number, y: number) =>
        getUsableVesselSlots(slotMaxAt(1, x)).includes(y);
      if (stackingSequence.size > 0) {
        const sequenced = new Set<string>();
        // Group the sequence cells by bay, preserving the per-bay slot order the
        // user configured, then walk the bays from the middle outwards. The
        // stacking sequence itself lists the forward bays before the aft bays,
        // which would push the destination zones (and the vessel's weight)
        // forward; walking the bays centre-out keeps the reserved zones
        // symmetric fore/aft while each bay's slots still follow the sequence.
        const cellsByBay = new Map<number, Array<[number, number]>>();
        Array.from(stackingSequence.entries())
          .sort((left, right) => left[1] - right[1])
          .forEach(([key]) => {
            const [x, y] = key.split("-").map(Number);
            if (usableAt(x, y)) {
              sequenced.add(key);
              if (!cellsByBay.has(x)) cellsByBay.set(x, []);
              cellsByBay.get(x)?.push([x, y]);
            }
          });
        const centerBay = (1 + floorColumns) / 2;
        const orderedBays = Array.from(cellsByBay.keys()).sort(
          (left, right) =>
            Math.abs(left - centerBay) - Math.abs(right - centerBay) ||
            left - right
        );
        orderedBays.forEach((x) =>
          cellsByBay.get(x)?.forEach((cell) => globalWalk.push(cell))
        );
        for (let x = 1; x <= floorColumns; x += 1) {
          for (const y of slotAlternation(slotsAt(x))) {
            const key = stackKey(x, y);
            if (!sequenced.has(key)) globalWalk.push([x, y]);
          }
        }
      } else if (plannerSetting.groupingPreference === "fullBeamStrips") {
        // Fill whole slot rows first, from the middle of the vessel outwards
        // (honouring the incomplete-layer preference), each row bay by bay.
        const rows = Array.from({ length: maxSlots }, (_, index) => index + 1);
        const rowCenter = (1 + maxSlots) / 2;
        rows.sort((left, right) => {
          const leftDistance = Math.abs(left - rowCenter);
          const rightDistance = Math.abs(right - rowCenter);
          const distanceComparison =
            plannerSetting.incompleteLayerPreference === "middleRowsFirst"
              ? leftDistance - rightDistance
              : rightDistance - leftDistance;
          return distanceComparison || right - left;
        });
        rows.forEach((y) => {
          for (let x = 1; x <= floorColumns; x += 1) {
            if (usableAt(x, y)) globalWalk.push([x, y]);
          }
        });
      } else if (plannerSetting.groupingPreference === "centerBlocks") {
        // Cluster the destination zones around the vessel's middle.
        const centerOutBays: number[] = [];
        for (
          let inner = Math.ceil(floorColumns / 2),
            outer = inner + 1;
          inner >= 1 || outer <= floorColumns;
          inner -= 1, outer += 1
        ) {
          const offsets =
            inner >= 1
              ? outer <= floorColumns
                ? [inner, outer]
                : [inner]
              : [outer];
          offsets.forEach((bay) => centerOutBays.push(bay));
        }
        centerOutBays.forEach((x) => {
          for (const y of slotAlternation(slotsAt(x))) globalWalk.push([x, y]);
        });
      } else {
        // smallPiles: barrier bays first (they hold the 1-TEU bases), then the
        // remaining bays from the centre outward so piles stay small.
        const barrierBays = new Set<number>();
        (selectedVesselForPlanner?.barriers ?? []).forEach(
          (barrierPosition: any) => {
            const [a, b] = barrierPosition ?? [];
            if (Number.isInteger(a)) barrierBays.add(a);
            if (Number.isInteger(b)) barrierBays.add(b);
          }
        );
        const centerOutBays: number[] = [];
        for (
          let inner = Math.ceil(floorColumns / 2),
            outer = inner + 1;
          inner >= 1 || outer <= floorColumns;
          inner -= 1, outer += 1
        ) {
          const offsets =
            inner >= 1
              ? outer <= floorColumns
                ? [inner, outer]
                : [inner]
              : [outer];
          offsets.forEach((bay) => centerOutBays.push(bay));
        }
        Array.from(barrierBays)
          .sort((a, b) => a - b)
          .forEach((x) => {
            for (const y of slotAlternation(slotsAt(x))) globalWalk.push([x, y]);
          });
        centerOutBays.forEach((x) => {
          if (barrierBays.has(x)) return;
          for (const y of slotAlternation(slotsAt(x))) globalWalk.push([x, y]);
        });
      }

      // Phase 1: every destination's paired 2-TEU footprints are claimed from
      // the walk first. Phase 2: the 1-TEU singles are claimed by reusing the
      // destination's own pair cells, so the 20ft bases sit directly beneath a
      // 40ft footprint and a 2-TEU bridge over two same-destination 1-TEUs is
      // possible, then any remaining single cells come from the free walk.
      reserveTargets.forEach(({ dest, pairTarget }) => {
        const cells = new Set<string>();
        claimCells(globalWalk, pairTarget, true, dest, cells);
        destinationPairs.set(dest, cells);
        const all = destinationReservations.get(dest) || new Set<string>();
        cells.forEach((key) => all.add(key));
        destinationReservations.set(dest, all);
      });
      reserveTargets.forEach(({ dest, singleTarget, bridgeBudget }) => {
        const cells = new Set<string>();
        const ownPairs = destinationPairs.get(dest);
        // Singles overlap their own pairs in full pair units, capped by the
        // 40ft capacity headroom, so a bridge never strands 40-foot cargo.
        const overlapSingles =
          2 * Math.min(Math.floor(singleTarget / 2), bridgeBudget);
        let claimed = 0;
        if (ownPairs) {
          for (const key of ownPairs) {
            if (claimed >= overlapSingles) break;
            if (cells.has(key)) continue;
            cells.add(key);
            claimed += 1;
          }
        }
        claimCells(globalWalk, singleTarget - claimed, false, dest, cells);
        destinationSingles.set(dest, cells);
        const all = destinationReservations.get(dest) || new Set<string>();
        cells.forEach((key) => all.add(key));
        destinationReservations.set(dest, all);
      });

      // Rank the destination's own footprints in the walk order so findPosition
      // fills the 20ft bases first and the 40ft footprints after.
      let walkRank = 0;
      reserveTargets.forEach(({ dest }) => {
        const cells = destinationReservations.get(dest);
        if (!cells) return;
        for (const [x, y] of globalWalk) {
          const key = stackKey(x, y);
          if (cells.has(key) && !reservationWalkRank.has(key)) {
            reservationWalkRank.set(key, walkRank);
            walkRank += 1;
          }
        }
      });
    }

    autoPlaceCancelledRef.current = false;

    setProcessing(true);
    setProcessedCount(0);
    setTotalCount(totalUnpositioned);

    let placed = 0;
    let proceeded = 0;

    // Placement is greedy and order-dependent: a 40ft can only bridge a pair of
    // 20ft that already exist, and a container may only use a stack once the
    // tier below is filled. A single pass over every destination therefore
    // leaves containers stranded even when the floor plan has room for them.
    // Run several rounds: each round retries whatever could not be placed, so
    // supports created by later containers unlock earlier ones, and the last
    // rounds are allowed to climb the full vessel height (ignoring the selected
    // layer limit and the per-destination reservations) so free upper-layer
    // floor is never left unused while containers remain to be loaded.
    let roundUnplaced = eligible;

    for (
      let round = 0;
      round < 5 && roundUnplaced.length > 0;
      round += 1
    ) {
      if (autoPlaceCancelledRef.current) {
        break;
      }

      const stillUnplaced: any[] = [];

      for (const container of roundUnplaced) {
        if (autoPlaceCancelledRef.current) {
          break;
        }

        setProcessedCount(++proceeded);

        // The first round respects the selected layer limit and the
        // destination reservations; the completion rounds stay within the same
        // layer limit but may spill across zones, so nothing is left unloaded
        // while the chosen stack height is still honoured.
        const found =
          round === 0
            ? findPosition(container)
            : findPosition(container, {
                maximumLayer: maximumLayers ?? positions.length,
                ignoreReservations: true,
              });
        if (!found) {
          stillUnplaced.push(container);
          continue;
        }

        const [l, x, y] = found;
        const position = [l, x, y, vesselId];

        addColumnWeight(x, spanOf(container), Number(container.weight) || 0);
        slotWeights[y] += Number(container.weight) || 0;
        addGroupSlotWeight(
          getDestinationSortKey(container),
          y,
          Number(container.weight) || 0
        );

        const containerId = normalizeId(container["@id"]);
        placedContainers.set(containerId, container);
        placementById.set(containerId, [l, x, y]);
        addToLayerProfile(container, l);

        const span = spanOf(container);
        for (let i = 0; i < span; i++) {
          occupied.add(cellKey(l, x + i, y));
          containerAtCell.set(cellKey(l, x + i, y), container);
          stackCohorts.set(stackKey(x + i, y), getDestinationSortKey(container));
        }

        await dispatch(
          updateSelectedContainer({
            ...container,
            position,
            vessel: selectedVesselForPlanner,
            loadingTerminal: filteredTerminalCall.terminal,
            loadingTerminalCall: filteredTerminalCall,
          })
        );
        placed++;
      }

      roundUnplaced = stillUnplaced;
    }

    setProcessedCount(totalUnpositioned);

    const terminalCallId = normalizeId(filteredTerminalCall["@id"]);

    const readDepartureStability = async () => {
      try {
        const response = await fetch(`/api/terminal_calls/${terminalCallId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!response.ok) return null;
        const freshTerminalCall = await response.json();
        const state =
          !selectedVesselForPlanner?.linkedVessel &&
          selectedVesselForPlanner?.barge
            ? freshTerminalCall?.bargeDepartureState
            : freshTerminalCall?.departureState;
        return state?.stability ?? null;
      } catch (err) {
        console.error("Failed to read departure stability:", err);
        return null;
      }
    };

    const recalculateVoyage = async (options?: { refresh?: boolean }) => {
      try {
        await fetch(`/api/terminal_call/${terminalCallId}/recalculate-voyage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } catch (err) {
        console.error("Failed to recalculate voyage terminal calls:", err);
      }

      if (options?.refresh !== false && voyage) {
        await refreshVoyageData(voyage);
        stayOnSelectedTerminalCall();
      }
    };

    const clearCells = (container: any, position: number[]) => {
      const [cl, cx, cy] = position;
      const span = spanOf(container);
      for (let i = 0; i < span; i++) {
        const key = cellKey(cl, cx + i, cy);
        occupied.delete(key);
        containerAtCell.delete(key);
        stackCohorts.delete(stackKey(cx + i, cy));
      }
      removeColumnWeight(cx, span, Number(container.weight) || 0);
      slotWeights[cy] -= Number(container.weight) || 0;
      removeGroupSlotWeight(
        getDestinationSortKey(container),
        cy,
        Number(container.weight) || 0
      );
      removeFromLayerProfile(container, cl);
    };

    const occupyCells = (container: any, position: number[]) => {
      const [l, x, y] = position;
      const span = spanOf(container);
      for (let i = 0; i < span; i++) {
        occupied.add(cellKey(l, x + i, y));
        containerAtCell.set(cellKey(l, x + i, y), container);
        stackCohorts.set(stackKey(x + i, y), getDestinationSortKey(container));
      }
      addColumnWeight(x, span, Number(container.weight) || 0);
      slotWeights[y] += Number(container.weight) || 0;
      addGroupSlotWeight(
        getDestinationSortKey(container),
        y,
        Number(container.weight) || 0
      );
      addToLayerProfile(container, l);
    };

    const correctStabilityIfNeeded = async (): Promise<{
      moved: number;
      unstable: boolean;
    }> => {
      let moved = 0;

      for (let round = 0; round < 3; round += 1) {
        if (autoPlaceCancelledRef.current) {
          return { moved, unstable: false };
        }

        const stability = await readDepartureStability();
        const loadedKg = Number(stability?.cog_loaded?.z ?? 0);
        const maxKg = Number(stability?.cog_loaded_max?.z ?? 0);
        const isUnstable = maxKg > 0 && maxKg !== 1 && loadedKg > maxKg;

        if (!isUnstable) {
          return { moved, unstable: false };
        }

        const movable = Array.from(placementById.entries())
          .map(([id, position]) => ({
            id,
            position,
            container: placedContainers.get(id),
          }))
          .filter(
            (
              entry
            ): entry is {
              id: string;
              position: number[];
              container: any;
            } => Boolean(entry.container) && entry.position[0] > 1
          )
          .sort((left, right) => {
            const weightComparison =
              getContainerWeightInTonnes(right.container) -
              getContainerWeightInTonnes(left.container);
            if (weightComparison !== 0) return weightComparison;
            return right.position[0] - left.position[0];
          });

        if (movable.length === 0) {
          return { moved, unstable: true };
        }

        setTotalCount(movable.length);
        setProcessedCount(0);

        let roundMoved = 0;
        for (const { id, container, position } of movable) {
          if (autoPlaceCancelledRef.current) {
            break;
          }
          if (roundMoved >= 20) break;

          const target = findPosition(container, {
            maximumLayer: position[0] - 1,
            preferLowestLayer: true,
          });
          if (!target) continue;

          clearCells(container, position);
          occupyCells(container, target);
          placementById.set(id, target);

          await dispatch(
            updateSelectedContainer({
              ...container,
              position: [...target, vesselId],
              vessel: selectedVesselForPlanner,
              loadingTerminal: filteredTerminalCall.terminal,
              loadingTerminalCall: filteredTerminalCall,
            })
          );
          roundMoved += 1;
          moved += 1;
          setProcessedCount(roundMoved);
        }

        if (roundMoved === 0) {
          return { moved, unstable: true };
        }

        // Recalculate server-side between rounds, but keep the UI in sync
        // only once all relocations are done (the POST itself recomputes the
        // departure state, which we read back via GET next round).
        await recalculateVoyage({ refresh: false });
      }

      const stability = await readDepartureStability();
      const loadedKg = Number(stability?.cog_loaded?.z ?? 0);
      const maxKg = Number(stability?.cog_loaded_max?.z ?? 0);
      return {
        moved,
        unstable: maxKg > 0 && maxKg !== 1 && loadedKg > maxKg,
      };
    };

    if (placed > 0) {
      await recalculateVoyage();

      if (placed === totalUnpositioned && !autoPlaceCancelledRef.current) {
        const stability = await readDepartureStability();
        const loadedKg = Number(stability?.cog_loaded?.z ?? 0);
        const maxKg = Number(stability?.cog_loaded_max?.z ?? 0);
        const needsReorganization = maxKg > 0 && maxKg !== 1 && loadedKg > maxKg;

        // The next step reorganizes containers to restore stability (e.g.
        // moving heavy containers to lower layers). Ask before proceeding.
        if (needsReorganization) {
          const confirmed = await confirmStabilityReorganization();
          if (autoPlaceCancelledRef.current) {
            setProcessing(false);
            setAutoPlacing(false);
            return;
          }
          if (!confirmed) {
            setProcessing(false);
            setAutoPlacing(false);
            setSnackbar({
              children: t("autoPlannerStabilitySkipped"),
              severity: "warning",
              variant: "filled",
            });
            return;
          }
        }

        const stabilityOutcome = await correctStabilityIfNeeded();

        if (stabilityOutcome.moved && voyage) {
          await recalculateVoyage();
        }

        setProcessing(false);
        setAutoPlacing(false);

        if (stabilityOutcome.unstable) {
          setSnackbar({
            children: t("autoPlannerStabilityUnstable"),
            severity: "warning",
            variant: "filled",
          });
        } else if (stabilityOutcome.moved) {
          setSnackbar({
            children: t("autoPlannerStabilityCorrected", {
              count: stabilityOutcome.moved,
            }),
            severity: "success",
            variant: "filled",
          });
        } else {
          setSnackbar({
            children: t("autoPositionAllSuccess", { count: placed }),
            severity: "success",
            variant: "filled",
          });
        }
      } else {
        setProcessing(false);
        setAutoPlacing(false);
        setSnackbar({
          children: t("autoPositionPartial", {
            count: placed,
            total: totalUnpositioned,
            remaining: totalUnpositioned - placed,
          }),
          severity: "warning",
          variant: "filled",
        });
      }
    } else {
      setProcessing(false);
      setAutoPlacing(false);
      setSnackbar({
        children: t("noPositionsAvailable"),
        severity: "info",
        variant: "filled",
      });
    }
  };

  const unpositionCrossCallOverlapContainers = async () => {
    const conflicting = crossCallOverlaps.containersToUnposition;

    if (conflicting.length === 0) {
      return;
    }

    setProcessing(true);
    setProcessedCount(0);
    setTotalCount(conflicting.length);

    let processed = 0;

    for (const container of conflicting) {
      await dispatch(
        updateSelectedContainer({
          ...container,
          position: null,
        })
      );
      setProcessedCount(++processed);
    }

    setProcessing(false);

    const terminalCallId = normalizeId(filteredTerminalCall["@id"]);

    try {
      await fetch(
        `/api/terminal_call/${terminalCallId}/recalculate-voyage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
    } catch (err) {
      console.error("Failed to recalculate voyage terminal calls:", err);
    }

    if (voyage) {
      await refreshVoyageData(voyage);
      stayOnSelectedTerminalCall();
    }

    setSnackbar({
      children: t("crossCallOverlapCleared", { count: processed }),
      severity: "success",
      variant: "filled",
    });
  };

  const stabilityLoadingContainers = useMemo(() => {
    if (!filteredTerminalCall) {
      return [];
    }

    const containersOnVessel = containerData.filter(
      (container: any) =>
        container.position?.length > 0 &&
        container.vessel?.["@id"] === selectedVesselForPlanner?.["@id"]
    );

    const unloadingList = filteredTerminalCall.unloadingContainers || [];

    if (unloadingList.length === 0) {
      return containersOnVessel;
    }

    const unloadIds = new Set(
      unloadingList.map((container: any) => container["@id"])
    );

    return containersOnVessel.filter(
      (container: any) => !unloadIds.has(container["@id"])
    );
  }, [containerData, filteredTerminalCall, selectedVesselForPlanner]);

  const containerLabels = useMemo(() => {
    const foundLabels = new Set<string>();

    return containerData
      .flatMap((container: any) => container.labels ?? [])
      .filter((label: any) => {
        const id = label["@id"];

        if (foundLabels.has(id)) {
          return false;
        }

        foundLabels.add(id);

        return true;
      });
  }, [containerData]);

  const containerDataTypes = useMemo(() => {
    const foundTypes = new Set<string>();
    const types: ContainerType[] = [];

    for (const container of containerData) {
      const containerType: any = (container as any).containerType;
      const id = containerType && (containerType["@id"] || containerType.id);

      if (containerType && id && !foundTypes.has(id)) {
        foundTypes.add(id);
        types.push(containerType);
      }
    }

    return types;
  }, [containerData]);

  const handleOpenContainerView = (container: Container) => {
    openModal({
      title: `${(container as any).uid}`,
      description: "Container information",
      form: "containerviewmodal",
      formProps: {
        container,
        vessels: vessels.data,
        terminals: terminals.data,
        terminalCalls,
        containerTypes: containerTypes.data,
        labels: labels.data,
        startPositionStream,
        parentVessel: selectedVesselForPlanner?.barge
          ? selectedVessel
          : undefined,
      },
    });
  };

  const handleDeleteSearch = () => {
    setSearchTerm("");
  };

  const vesselPositionNumbers = useMemo(() => {
    const positions = selectedVesselForPlanner?.positions;

    if (!Array.isArray(positions) || positions.length === 0) {
      return [];
    }

    const maxPosition = positions.reduce((max: number, layer: any) => {
      if (!Array.isArray(layer)) {
        return max;
      }

      return Math.max(max, layer.length);
    }, 0);

    const numbers = Array.from({ length: maxPosition }, (_, index) => index + 1);

    return dockingSide === DOCKING_SIDE_STARBOARD
      ? [...numbers].reverse()
      : numbers;
  }, [selectedVesselForPlanner, dockingSide]);

  const containerWeightPerPosition = useMemo(() => {
    const weights: Record<number, number> = {};

    vesselPositionNumbers.forEach((positionNumber) => {
      weights[positionNumber] = 0;
    });

    if (!selectedVesselForPlanner || !Array.isArray(containerData)) {
      return weights;
    }

    const selectedTerminalCallUnloadingContainerIds = new Set(
      planType === PLAN_TYPE_LOADING && filteredTerminalCall?.unloadingContainers
        ? filteredTerminalCall.unloadingContainers.map(
            (container: any) => container?.["@id"]
          )
        : []
    );

    containerData.forEach((container: any) => {
      if (!container?.position || !Array.isArray(container.position)) {
        return;
      }

      if (container?.dummy) {
        return;
      }

      if (
        planType === PLAN_TYPE_LOADING &&
        selectedTerminalCallUnloadingContainerIds.has(container?.["@id"])
      ) {
        return;
      }

      const selectedVesselId = normalizeId(selectedVesselForPlanner?.["@id"]);
      const containerVesselId = normalizeId(container?.vessel?.["@id"]);

      if (
        selectedVesselId &&
        containerVesselId &&
        selectedVesselId !== containerVesselId
      ) {
        return;
      }

      const [, xPosition] = container.position;
      const teu = Number(container?.containerType?.teu ?? 1);
      const span = teu === 4 ? 2 : Math.max(teu, 1);
      const weightInTonnes = Number(container?.weight ?? 0) / 1000;

      if (!xPosition || weightInTonnes <= 0) {
        return;
      }

      const weightPerPosition = weightInTonnes / span;

      for (let index = 0; index < span; index++) {
        const positionNumber = Number(xPosition) + index;

        if (typeof weights[positionNumber] !== "number") {
          weights[positionNumber] = 0;
        }

        weights[positionNumber] += weightPerPosition;
      }
    });

    return weights;
  }, [
    containerData,
    filteredTerminalCall,
    planType,
    selectedVesselForPlanner,
    vesselPositionNumbers,
  ]);

  const renderLoadingContainerGrid = (container: Container) => (
    <Grid
      key={(container as any)["@id"]}
      className="container-planner-loading-container-row"
    >
      <Grid
        item
        className={`container-planner-grid ${
          (container as any).containerType?.teu > 1
            ? `container-planner-grid-teu-${(container as any).containerType.teu}`
            : ""
        }`}
        data-container-id={(container as any)["@id"]}
        data-container-teu={(container as any).containerType?.teu ?? 1}
        onDragStart={dragStart}
        onDragEnd={handleDragEnd}
        draggable
        sx={{
          backgroundColor: containerColorHandler(container) + " !important",
          color: textColorHandler(containerColorHandler(container)),
          position: "relative",
        }}
        onDoubleClick={() => handleOpenContainerView(container)}
      >
        {containerUidFormatter((container as any).uid).letters}
        <br />
        {containerUidFormatter((container as any).uid).numbers}

        <Grid
          item
          sx={{
            display: "flex",
            gap: 1,
            position: "absolute",
            right: "4px",
            top: "4px",
          }}
        >
          <LabelDots labels={(container as any).labels} />
        </Grid>
      </Grid>

      {(container as any).empty ? (
        <Box
          sx={{
            border: "2px solid #666666",
            padding: "1px",
            paddingLeft: "3px",
            paddingRight: "3px",
            borderRadius: "8px",
            textAlign: "center",
            width: "28px",
          }}
        >
          <Typography
            sx={{
              fontSize: "10px !important",
              fontWeight: 600,
              color: "#666666",
            }}
          >
            MT
          </Typography>
        </Box>
      ) : null}

      <Box ml={1}>
        <Typography>
          {(container as any).weight && (container as any).weight > 0
            ? ((container as any).weight / 1000).toFixed(3)
            : 0}{" "}
          T
        </Typography>

        <Typography>{(container as any)?.containerType?.name}</Typography>
      </Box>
    </Grid>
  );

  const RenderGrid = (
    layer: number,
    xAxis: number,
    yAxis: number,
    gridCount: number,
    grids: number[],
    nextGridCount?: number
  ) => {
    const isGhostGrid =
      (gridCount === 2 && (yAxis === 1 || yAxis === 4)) ||
      (gridCount === 3 && (yAxis === 1 || yAxis === 5));
    const containerWithPosition = findContainerByPosition([
      layer,
      xAxis,
      yAxis,
    ]) as any;
    const isDummy = Boolean(containerWithPosition?.dummy);
    const isAnArrivalContainer =
      isContainerFromArrivalContainers(containerWithPosition);
    const isPositioned = Boolean(containerWithPosition);
    const isSpecialGhostGrid =
      isGhostGrid && xAxis === grids.length && gridCount === 3;

    const inLoadingRow = (element: HTMLElement) =>
      Boolean(element.closest(".container-planner-loading-container-row"));

    const gridKey = `${layer}-${xAxis}-${yAxis}`;

    const { letters = "", numbers = "" } = containerWithPosition
      ? containerUidFormatter(containerWithPosition.uid)
      : { letters: "", numbers: "" };

    const matchesLabels =
      !containerWithPosition ||
      filterLabels.length === 0 ||
      containerWithPosition.labels?.some((label: any) =>
        filterLabels.includes(label["@id"])
      );

    const matchesTypes =
      !containerWithPosition ||
      filterTypes.length === 0 ||
      filterTypes.includes(containerWithPosition.containerType?.["@id"]);

    const matchesSearch =
      (searchTerm.length < 3 ||
        letters.toLowerCase().includes(searchTerm.toLowerCase()) ||
        numbers.toLowerCase().includes(searchTerm.toLowerCase()) ||
        containerWithPosition?.uid
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) &&
      matchesLabels &&
      matchesTypes;

    const className = [
      "container-planner-grid",
      isPositioned &&
        !isDummy &&
        `container-planner-grid-teu-${containerWithPosition?.containerType.teu}`,
      isDummy && "container-planner-grid--dummy",
      isAnArrivalContainer && "grid-arrival-container",
      isGhostGrid && "container-planner-ghost-grid",
      isSpecialGhostGrid && "container-planner-ghost-grid--small",
    ]
      .filter(Boolean)
      .join(" ");

    const canDrag =
      !isDummy &&
      planType === PLAN_TYPE_LOADING &&
      ((Boolean(containerWithPosition) && !isAnArrivalContainer) ||
        (isAnArrivalContainer &&
          shiftingModeEnabled &&
          !containerWithPosition?.shiftedDate));

    return (
      <Grid
        id={
          containerWithPosition
            ? `container-${normalizeId(containerWithPosition["@id"])}`
            : `grid-${layer}-${xAxis}-${yAxis}`
        }
        key={gridKey}
        item
        sx={{
          backgroundColor: `${containerColorHandler(
            containerWithPosition
          )} !important`,
          color: textColorHandler(containerColorHandler(containerWithPosition)),
          opacity: matchesSearch ? 1 : 0.5,
        }}
        className={className}
        data-grid-layer={layer}
        data-grid-x={xAxis}
        data-grid-y={yAxis}
        data-grid-onboard={isAnArrivalContainer}
        onDragOver={(event: React.DragEvent<HTMLElement>) => {
          event.preventDefault();

          const element = event.currentTarget;

          if (
            !isPositioned &&
            !inLoadingRow(element) &&
            containerPositionHandler(layer, xAxis, yAxis, grids, nextGridCount)
          ) {
            element.classList.add("drag-over-grid");
            element.classList.add(
              `container-planner-grid-teu-${draggedContainer.current?.teu}`
            );
          }
        }}
        onDragLeave={(event: React.DragEvent<HTMLElement>) => {
          const element = event.currentTarget;

          element.classList.remove("drag-over-grid");

          if (!isPositioned) {
            element.classList.remove(
              `container-planner-grid-teu-${draggedContainer.current?.teu}`
            );
          }
        }}
        onDrop={(event: React.DragEvent<HTMLElement>) => {
          event.preventDefault();

          const element = event.currentTarget;

          if (
            !isPositioned &&
            containerPositionHandler(layer, xAxis, yAxis, grids, nextGridCount)
          ) {
            containerStateHandler(event);
          }

          element.classList.remove("drag-over-grid");

          if (
            !draggedContainer.current?.position &&
            !element.getAttribute("data-container-id")
          ) {
            element.classList.remove(
              `container-planner-grid-teu-${draggedContainer.current?.teu}`
            );
          }
        }}
        onDragStart={dragStart}
        onDragEnd={handleDragEnd}
        data-container-id={
          containerWithPosition ? containerWithPosition["@id"] : undefined
        }
        data-container-teu={
          containerWithPosition && containerWithPosition.containerType
            ? containerWithPosition.containerType.teu
            : 1
        }
        draggable={canDrag}
        onClick={(event: any) => {
          event.preventDefault();
          event.stopPropagation();

          if (!positionModeEnabled) {
            return;
          }

          if (
            !isPositioned &&
            containerPositionHandler(layer, xAxis, yAxis, grids, nextGridCount)
          ) {
            dragStart(event);
            handleEmptyGridClick(layer, xAxis, yAxis);
          }
        }}
        onDoubleClick={() => {
          if (containerWithPosition && !isDummy) {
            handleOpenContainerView(containerWithPosition);
          }
        }}
      >
        {containerWithPosition ? (
          isDummy ? (
            <Grid
              container
              position="relative"
              height="100%"
              alignItems="center"
              justifyContent="center"
            />
          ) : (
            <Grid container position="relative" height="100%" alignItems="center">
              <Typography fontSize="inherit">
                {!containerGridLabelSwitch ? (
                  <>
                    {containerUidFormatter(containerWithPosition.uid).letters}
                    <br />
                    {containerUidFormatter(containerWithPosition.uid).numbers}
                  </>
                ) : (
                  <>
                    {containerWithPosition?.weight &&
                    containerWithPosition.weight > 0
                      ? (containerWithPosition.weight / 1000).toFixed(3)
                      : "0"}{" "}
                    T
                  </>
                )}
              </Typography>

              <Grid
                item
                sx={{
                  display: "flex",
                  gap: 1,
                  position: "absolute",
                  right: 0,
                  top: 0,
                }}
              >
                <LabelDots labels={containerWithPosition.labels} />
              </Grid>

              {(planType === PLAN_TYPE_LOADING &&
                containerWithPosition?.loadedDate) ||
              (planType === PLAN_TYPE_UNLOADING &&
                containerWithPosition?.unloadedDate) ? (
                <CheckCircleIcon
                  sx={{
                    stroke: "#ffffff",
                    strokeWidth: 0.5,
                    backgroundColor: "#ffffff",
                    borderRadius: "100%",
                    position: "absolute",
                    left: "-7px",
                    top: "-10px",
                  }}
                  color="success"
                  className="container-planner-grid-icon"
                />
              ) : null}

              {containerWithPosition?.empty ? (
                <Box
                  sx={{
                    border: `2px solid ${textColorHandler(
                      containerColorHandler(containerWithPosition)
                    )}`,
                    padding: "1px",
                    borderRadius: "2px",
                    position: "absolute",
                    right: containerWithPosition.shiftedDate ? "25px" : 0,
                    top: "3px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "10px !important",
                      fontWeight: 600,
                      color: textColorHandler(
                        containerColorHandler(containerWithPosition)
                      ),
                    }}
                  >
                    MT
                  </Typography>
                </Box>
              ) : null}

              {planType !== PLAN_TYPE_SHIFTERS &&
              containerWithPosition?.shiftedDate ? (
                <SwapHorizontalCircleIcon
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: "100%",
                    position: "absolute",
                    right: 0,
                    top: "5px",
                  }}
                  color="secondary"
                  className="container-planner-grid-icon--shifter"
                />
              ) : null}
            </Grid>
          )
        ) : null}
      </Grid>
    );
  };

  const RenderTerminalDetail = (terminalCall: TerminalCall) => {
    return (
      <>
        <TerminalCallDetail
          terminalCall={terminalCall}
          terminals={terminals.data}
          vessels={vessels.data}
          labels={labels.data}
          voyages={voyages.data}
          containers={containers.data || []}
          callback={refreshSelectedTerminalCalls}
        />

        <TerminalCallOverviewStats
          terminalCall={terminalCall}
          allContainers={containers.data || []}
          selectedVesselForPlanner={selectedVesselForPlanner}
          t={t}
        />
      </>
    );
  };

  const renderTerminalCallListPanel = () => {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: TERMINAL_LIST_BORDER_RADIUS,
          border: "1px solid rgba(15, 23, 42, 0.08)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          backgroundColor: "#ffffff",
        }}
      >
        <Box
          sx={{
            p: 1.25,
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
            backgroundColor: "#ffffff",
            flexShrink: 0,
          }}
        >
          <Stack spacing={1} alignItems="stretch">
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <PlannerViewSwitch activeView="stacker" disabled={processing} t={t} />
            </Box>

            <Box sx={{ width: "100%", minWidth: 0 }}>
              <VoyagePicker allowRefresh={true} />
            </Box>
          </Stack>
        </Box>

        <Box
          className="container-planner-terminal-call-items"
          sx={(theme) => ({
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: 1,
            backgroundColor: "#ffffff",
            border: terminalColumnDragOver
              ? `1px dashed ${theme.palette.primary.main}`
              : "1px solid transparent",
            boxShadow: terminalColumnDragOver
              ? `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}`
              : "none",
            transition:
              "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
          })}
          onDragOver={handleTerminalColumnDragOver}
          onDragLeave={handleTerminalColumnDragLeave}
          onDrop={(event: React.DragEvent<HTMLElement>) => {
            setTerminalColumnDragOver(false);
            event.preventDefault();
          }}
        >
          {hasDuplicateTimings && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
              sx={{ mb: 1.5, py: 0, px: 1.5, cursor: "pointer" }}
              onClick={() => setSameTimeDialogOpen(true)}
            >
              <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                {t("timeConflictDetected", { count: duplicateGroups.length })}
              </Typography>
            </Alert>
          )}
          {terminalCallGroups.map(([key, value]) => {
            const todayGroup = isTodayGroup(key);
            const groupTerminalCalls = value as TerminalCall[];
            const groupDuplicateTcs = groupTerminalCalls.filter(
              (tc: any) => sameDateTimeWarnings[tc["@id"]]
            );
            const groupHasDuplicates = groupDuplicateTcs.length > 0;

            return (
              <Box key={key as string} sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 0.25, mb: 1 }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 950,
                      color: todayGroup ? "primary.main" : "text.primary",
                    }}
                  >
                    {getTerminalCallGroupTitle(key)}
                  </Typography>

                  {todayGroup ? (
                    <Chip
                      size="small"
                      label={t("today")}
                      sx={(theme) => ({
                        height: 20,
                        borderRadius: "7px",
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        color: "primary.main",
                        fontSize: "0.56rem",
                        fontWeight: 950,
                        textTransform: "uppercase",
                        "& .MuiChip-label": {
                          px: 0.75,
                        },
                      })}
                    />
                  ) : null}
                </Stack>

                <Box sx={{ position: "relative" }}>
                  <Stack spacing={0.85}>
                    {groupTerminalCalls.map((terminalCall: any) => {
                      const terminalId = terminalCall.terminal?.["@id"] || null;

                    const isHighlighted =
                      terminalColumnDragOver &&
                      !!draggedContainer.current?.loadingTerminalId &&
                      terminalId === draggedContainer.current.loadingTerminalId;

                    const isDimmed = terminalColumnDragOver && !isHighlighted;
                    const terminalCallDone = isTerminalCallDone(terminalCall);
                    const terminalColor = getPlannerTerminalCallColor(terminalCall);
                    const selected =
                      filteredTerminalCall &&
                      filteredTerminalCall["@id"] === terminalCall["@id"];

                    const primaryLabel = getTerminalCallPrimaryLabel(terminalCall);

                    const badgeLabel = getPlannerTerminalStatusLabel(
                      terminalCall,
                      terminalCallDone,
                      primaryLabel,
                      t
                    );

                    const badgeColor = terminalCallDone
                      ? TERMINAL_UNLOADING_COLOR
                      : primaryLabel?.color ||
                        (String(terminalCall.status || "").toLowerCase() ===
                        "planned"
                          ? PLANNED_BADGE_COLOR
                          : terminalColor);

                    const terminalCallCounts =
                      getTerminalCallContainerCounts(terminalCall);

                    return (
                      <Paper
                        key={terminalCall["@id"]}
                        className={`js-terminal-call-parent ${
                          selected ? "planning-selected-terminal-call" : ""
                        }`}
                        elevation={0}
                        data-terminal-call-id={terminalCall["@id"]}
                        data-terminal-id={terminalId || undefined}
                        onDrop={(event: React.DragEvent<HTMLElement>) => {
                          containerChangeLoadingTerminalCallHandler(event);
                          setTerminalColumnDragOver(false);
                        }}
                        onClick={selectedTerminalCallHandler}
                        sx={(theme) => ({
                          position: "relative",
                          minHeight: 76,
                          p: 0.85,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          borderRadius: TERMINAL_LIST_BORDER_RADIUS,
                          cursor: "pointer",
                          opacity: isDimmed ? 0.35 : 1,
                          backgroundColor: terminalCallDone
                            ? alpha(theme.palette.success.main, 0.12)
                            : alpha(terminalColor, selected ? 0.28 : 0.18),
                          border: selected
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${alpha(terminalColor, 0.45)}`,
                          boxShadow: isHighlighted
                            ? `0 0 0 2px ${alpha(
                                theme.palette.primary.main,
                                0.85
                              )}`
                            : selected
                            ? `0 7px 20px ${alpha(
                                theme.palette.primary.main,
                                0.22
                              )}`
                            : "0 4px 14px rgba(15, 23, 42, 0.08)",
                          transform: isHighlighted ? "scale(1.02)" : "none",
                          transition: "all 140ms ease",
                          userSelect: "none",
                          overflow: "hidden",
                          "&:hover": {
                            transform: isHighlighted
                              ? "scale(1.02)"
                              : "translateY(-1px)",
                            boxShadow: selected
                              ? `0 8px 22px ${alpha(
                                  theme.palette.primary.main,
                                  0.24
                                )}`
                              : "0 8px 20px rgba(15, 23, 42, 0.12)",
                          },
                        })}
                      >
                        <Box
                          sx={(theme) => ({
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            border: terminalCallDone
                              ? `2px solid ${theme.palette.success.main}`
                              : `3px solid ${terminalColor}`,
                            backgroundColor: terminalCallDone
                              ? alpha(theme.palette.success.main, 0.12)
                              : "rgba(255,255,255,0.72)",
                          })}
                        >
                          {terminalCallDone ? (
                            <CheckCircleIcon
                              color="success"
                              sx={{ fontSize: 21 }}
                            />
                          ) : null}
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "0.86rem",
                              fontWeight: 950,
                              lineHeight: 1.1,
                              color: terminalCallDone
                                ? "success.dark"
                                : "text.primary",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {formatDateToString(terminalCall.planned, "HH:mm")}
                          </Typography>

                          <Typography
                            noWrap
                            sx={{
                              mt: 0.3,
                              fontSize: "0.78rem",
                              fontWeight: 900,
                              color: "text.primary",
                            }}
                          >
                            {getPlannerTerminalCallNumber(terminalCall)}
                          </Typography>

                          <Typography
                            noWrap
                            sx={{
                              mt: 0.2,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              color: "text.secondary",
                            }}
                          >
                            {getPlannerTerminalCallName(terminalCall)}
                          </Typography>
                        </Box>

                        {badgeLabel ? (
                          <Box
                            sx={{
                              px: 0.65,
                              py: 0.3,
                              borderRadius: "8px",
                              flexShrink: 0,
                              maxWidth: 76,
                              backgroundColor: badgeColor,
                              color: textColorHandler(badgeColor),
                              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.16)",
                            }}
                          >
                            <Typography
                              noWrap
                              sx={{
                                fontSize: "0.56rem",
                                fontWeight: 950,
                                lineHeight: 1,
                                textTransform: "uppercase",
                              }}
                            >
                              {badgeLabel}
                            </Typography>
                          </Box>
                        ) : null}

                        <TerminalCallContainerCounts
                          unloading={terminalCallCounts.unloading}
                          loading={terminalCallCounts.loading}
                        />
                      </Paper>
                    );
                  })}
                </Stack>
                {groupHasDuplicates && (
                  <Box
                    onClick={() => setSameTimeDialogOpen(true)}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 1,
                      background: (theme) =>
                        `linear-gradient(135deg, ${alpha(
                          theme.palette.warning.main,
                          0.20
                        )} 0%, ${alpha(
                          theme.palette.warning.light,
                          0.12
                        )} 100%)`,
                      backdropFilter: "blur(1px)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      transition: "background 0.2s ease",
                      "&:hover": {
                        background: (theme) =>
                          `linear-gradient(135deg, ${alpha(
                            theme.palette.warning.main,
                            0.28
                          )} 0%, ${alpha(
                            theme.palette.warning.light,
                            0.18
                          )} 100%)`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        backgroundColor: "rgba(255,255,255,0.85)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                        maxWidth: "90%",
                      }}
                    >
                      <WarningAmberIcon
                        sx={{ fontSize: 22, color: "warning.dark", flexShrink: 0 }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 800,
                            color: "warning.dark",
                            lineHeight: 1.2,
                          }}
                        >
                          {t("duplicateTimeWarning")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.62rem",
                            fontWeight: 600,
                            color: "text.secondary",
                            mt: 0.25,
                          }}
                        >
                          {t("clickToResolve")}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                </Box>
              </Box>
            );
          })}

          {terminalCallGroups.length === 0 ? (
            <Alert severity="info">
              {t("noTerminalCallsFoundForSelectedVoyage")}
            </Alert>
          ) : null}
        </Box>

        <Box
          component={Paper}
          elevation={0}
          sx={{
            p: 1,
            borderRadius: 0,
            borderTop: "1px solid rgba(15, 23, 42, 0.08)",
            backgroundColor: "#ffffff",
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() =>
              openModal({
                title: t("addNewPlanning"),
                subtitle: t("addNewPlanningDescription"),
                description: "Creates new planning",
                form: "simpleterminalcall",
                formProps: {
                  vessel: selectedVessel,
                  showTerminalField: true,
                },
                size: "compact",
              })
            }
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 850,
            }}
          >
            {t("planning")}
          </Button>
        </Box>
      </Paper>
    );
  };

  return (
    <Layout showNotesButton notesSlideoutOverlay={false}>
      <Box
        sx={{
          height: "calc(100vh - 110px)",
          display: "grid",
          gridTemplateColumns: "320px minmax(0, 1fr)",
          gap: 1.5,
          p: 1.5,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {renderTerminalCallListPanel()}
        </Box>

        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Grid
            item
            className="container-planner-menu-wrapper"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="4px"
            sx={{
              flexShrink: 0,
            }}
          >
            <Box
              component={Paper}
              display="flex"
              alignItems="center"
              sx={{
                paddingLeft: 1,
                height: "100%",
                width: "45vh",
                gap: 1,
                borderRadius: WRAPPER_BORDER_RADIUS,
                paddingRight: "8px !important",
              }}
            >
              <IconButton
                onClick={() =>
                  openModal({
                    title: t("manageTerminals"),
                    subtitle: t("manageTerminalsDescription"),
                    form: "terminallist",
                    size: "wide",
                  })
                }
                size="small"
                title={t("manageTerminals")}
                aria-label={t("manageTerminals")}
              >
                <PrecisionManufacturingIcon fontSize="small" />
              </IconButton>

              <IconButton onClick={handleOpenSettings} size="small">
                <TuneIcon fontSize="small" />
              </IconButton>

              <Tooltip title={t("totalWeightTonPerPosition")} arrow>
                <IconButton
                  size="small"
                  onClick={() => setShowPositionWeights((current) => !current)}
                  color={showPositionWeights ? "primary" : "default"}
                  aria-label={t("totalWeightTonPerPosition")}
                  sx={(theme) => ({
                    width: 34,
                    height: 34,
                    borderRadius: WRAPPER_BORDER_RADIUS,
                    border: showPositionWeights
                      ? `1px solid ${theme.palette.primary.main}`
                      : "1px solid rgba(15, 23, 42, 0.12)",
                    backgroundColor: showPositionWeights
                      ? alpha(theme.palette.primary.main, 0.12)
                      : "transparent",
                    "&:hover": {
                      backgroundColor: showPositionWeights
                        ? alpha(theme.palette.primary.main, 0.18)
                        : "rgba(15, 23, 42, 0.04)",
                    },
                  })}
                >
                  <ScaleIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <ContainerSearch
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
              />
            </Box>

            <Grid
              component={Paper}
              container
              justifyContent="space-between"
              alignItems="center"
              flexWrap="nowrap"
              sx={{
                gap: 1,
                px: 1,
                borderRadius: WRAPPER_BORDER_RADIUS,
              }}
            >
              <Grid item>
                <ToggleButtonGroup
                  value={planType}
                  exclusive
                  disabled={processing}
                  onChange={(event, nextPlanType) => {
                    if (!nextPlanType) {
                      return;
                    }

                    setPlanType(nextPlanType);
                    setContainerColorType(
                      nextPlanType === PLAN_TYPE_UNLOADING
                        ? LOADING_CONTAINER_COLOR
                        : UNLOADING_CONTAINER_COLOR
                    );
                  }}
                  sx={{
                    ...PLANNER_SWITCH_SX,
                    "& .MuiToggleButtonGroup-grouped": {
                      ...PLANNER_SWITCH_SX["& .MuiToggleButtonGroup-grouped"],
                      minWidth: 104,
                    },
                  }}
                >
                  <ToggleButton value={PLAN_TYPE_LOADING}>
                    {t("stowagePlan")}
                  </ToggleButton>

                  <ToggleButton value={PLAN_TYPE_LOADING_ONLY}>
                    {t("loading")}
                  </ToggleButton>

                  <ToggleButton value={PLAN_TYPE_UNLOADING}>
                    {t("unloading")}
                  </ToggleButton>

                  <ToggleButton
                    value={PLAN_TYPE_SHIFTERS}
                    disabled={shiftedContainers.length === 0}
                  >
                    {t("shifters")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              <Grid
                item
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handlePdfButtonClick}
                  title={pdfButtonLabel}
                  aria-label={pdfButtonLabel}
                  sx={{
                    height: 40,
                    px: 1.5,
                    gap: 1,
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    borderRadius: "10px",
                    minWidth: showPdfButtonArrow ? 240 : 200,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: showPdfButtonArrow
                      ? "space-between"
                      : "center",
                    borderColor: "rgba(15, 23, 42, 0.12)",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      minWidth: 0,
                    }}
                  >
                    <PictureAsPdfSharpIcon fontSize="small" />
                    <EmailIcon fontSize="small" />

                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 700,
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pdfButtonLabel}
                    </Typography>
                  </Box>

                  {showPdfButtonArrow ? (
                    <KeyboardArrowDownIcon
                      fontSize="small"
                      sx={{
                        ml: 0.5,
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                </Button>

                <Menu
                  anchorEl={pdfMenuAnchor}
                  open={Boolean(pdfMenuAnchor)}
                  onClose={handlePdfMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <MenuItem
                    onClick={() => handlePdfPlanChoice(PLAN_TYPE_LOADING_ONLY)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PictureAsPdfSharpIcon fontSize="small" />
                      <EmailIcon fontSize="small" />
                      <Typography>{t("loading")} PDF / Email</Typography>
                    </Box>
                  </MenuItem>

                  <MenuItem
                    onClick={() => handlePdfPlanChoice(PLAN_TYPE_UNLOADING)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PictureAsPdfSharpIcon fontSize="small" />
                      <EmailIcon fontSize="small" />
                      <Typography>{t("unloading")} PDF / Email</Typography>
                    </Box>
                  </MenuItem>
                </Menu>
              </Grid>

              <Grid
                item
                className="container-planner-vessel-switch"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                  "& .MuiDivider-root": {
                    height: 28,
                    borderColor: "rgba(15, 23, 42, 0.12)",
                  },
                }}
              >
                <ToggleButtonGroup
                  value={dockingSide}
                  exclusive
                  onChange={dockingSideChange}
                  disabled={planType === PLAN_TYPE_SHIFTERS}
                  sx={PLANNER_SWITCH_SX}
                >
                  <ToggleButton value={DOCKING_SIDE_PORT}>
                    {t(DOCKING_SIDE_PORT)}
                  </ToggleButton>

                  <ToggleButton value={DOCKING_SIDE_STARBOARD}>
                    {t(DOCKING_SIDE_STARBOARD)}
                  </ToggleButton>
                </ToggleButtonGroup>

                <Divider orientation="vertical" />

                {selectedVessel ? (
                  <ToggleButtonGroup
                    value={selectedVesselForPlanner}
                    exclusive
                    onChange={selectedVesselForPlannerHandler}
                    disabled={planType === PLAN_TYPE_SHIFTERS}
                    sx={{
                      ...PLANNER_SWITCH_SX,
                      "& .MuiToggleButtonGroup-grouped": {
                        ...PLANNER_SWITCH_SX["& .MuiToggleButtonGroup-grouped"],
                        minWidth: 104,
                        textTransform: "none",
                      },
                    }}
                  >
                    <ToggleButton value={selectedVessel}>
                      {selectedVessel.name}
                    </ToggleButton>

                    {selectedVessel.linkedVessel ? (
                      <ToggleButton value={selectedVessel.linkedVessel}>
                        {selectedVessel.linkedVessel.name}
                      </ToggleButton>
                    ) : null}
                  </ToggleButtonGroup>
                ) : null}
              </Grid>
            </Grid>
          </Grid>

          {containers.loading && (
            <Box
              sx={{
                width: "100%",
                mt: 0.5,
                flexShrink: 0,
              }}
            >
              <LinearProgress
                sx={{
                  height: 3,
                  borderRadius: 9999,
                }}
              />
            </Box>
          )}

          <Grid
            container
            alignItems="stretch"
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Grid
              item
              xs={12}
              sm={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                minHeight: 0,
                height: "100%",
                maxHeight: "100%",
                overflow: "hidden",
                padding: 0,
                paddingRight: "10px",
              }}
            >
              <Grid
                item
                className="container-planner-terminal-information"
                component={Paper}
                sx={{
                  borderRadius: WRAPPER_BORDER_RADIUS,
                  overflow: "hidden",
                  padding: "5px",
                }}
              >
                {filteredTerminalCall && updatedTerminalCall ? (
                  <>
                    {RenderTerminalDetail(updatedTerminalCall as any)}

                    {((filteredTerminalCall.loadingContainers?.length ?? 0) >
                      0 ||
                      (filteredTerminalCall.unloadingContainers?.length ?? 0) >
                        0) && (
                      <Button
                        fullWidth
                        color="success"
                        size="small"
                        variant="contained"
                        sx={{
                          marginTop: 1,
                        }}
                        onClick={(event) => {
                          event.stopPropagation();

                          openModal({
                            title: t("terminalCallCompletion"),
                            description: "Mark terminal call as complete",
                            form: "terminalcallcompletion",
                            formProps: { terminalCall: filteredTerminalCall },
                          });
                        }}
                      >
                        {t("completed")}
                      </Button>
                    )}
                  </>
                ) : null}
              </Grid>

              <Grid
                container
                component={Paper}
                padding={1}
                sx={{
                  minHeight: "4vh",
                  height: "auto",
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  borderRadius: WRAPPER_BORDER_RADIUS,
                  padding: "5px",
                }}
              >
                <FilterChips
                  searchTerm={searchTerm}
                  activeFilters={activeFilters}
                  labelOptions={containerLabels.map((label: any) => ({
                    id: label["@id"],
                    name: label.name,
                    color: label.color,
                  }))}
                  typeOptions={containerDataTypes.map((type: any) => ({
                    id: type["@id"],
                    name: type.name,
                  }))}
                  onDeleteSearch={handleDeleteSearch}
                  onDeleteFilter={handleDeleteFilter}
                />

                <IconButton onClick={handleOpenFilters} size="small">
                  <FilterAltIcon fontSize="small" />
                </IconButton>
              </Grid>

              <Grid
                item
                className="container-planner-loading-container-wrapper"
                component={Paper}
                data-container-list={true}
                sx={{
                  flex: "1 1 0",
                  minHeight: 0,
                  height: "100%",
                  maxHeight: "100%",
                  overflow: "hidden",
                  borderRadius: WRAPPER_BORDER_RADIUS,
                  padding: "5px",
                  display: "flex",
                  flexDirection: "column",
                }}
                onDrop={(event: React.DragEvent<HTMLElement>) => {
                  event.preventDefault();
                  containerStateHandler(event);
                }}
                onDragOver={(event: any) => dragOver(event)}
              >
                {selectedVessel && filteredTerminalCall && !containers.error ? (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      maxHeight: "30px",
                      color: "#666666",
                    }}
                  >
                    {(() => {
                      const planned =
                        planType === PLAN_TYPE_LOADING
                          ? filteredTerminalCall.loadingContainers?.length ?? 0
                          : filteredTerminalCall.unloadingContainers?.length ?? 0;
                      const placed = numberOfContainersWithPosition();

                      if (!planned || planType !== PLAN_TYPE_LOADING) {
                        return null;
                      }

                      return (
                        <Typography
                          fontSize="0.875rem"
                          fontWeight={700}
                          color="primary"
                        >
                          {planType === PLAN_TYPE_LOADING
                            ? t("loaded")
                            : t("unloaded")}
                          : {placed}/{planned}
                        </Typography>
                      );
                    })()}

                    {(() => {
                      const planned =
                        planType === PLAN_TYPE_LOADING
                          ? filteredTerminalCall.loadingContainers?.length ?? 0
                          : filteredTerminalCall.unloadingContainers?.length ?? 0;

                      if (!planned || planType !== PLAN_TYPE_LOADING) {
                        return null;
                      }

                      return (
                        <Typography
                          fontSize="0.875rem"
                          fontWeight={700}
                          color="secondary"
                        >
                          {t("empty")}: {`${numberOfEmptyContainers()}/${planned}`}
                        </Typography>
                      );
                    })()}

                    {numberOfContainersWithPosition() &&
                    planType === PLAN_TYPE_LOADING ? (
                      <Button
                        size="small"
                        color="secondary"
                        onClick={() => setShowUnpositionConfirm(true)}
                        startIcon={<RotateLeftIcon />}
                      >
                        {t("reset")}
                      </Button>
                    ) : null}
                  </Grid>
                ) : null}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    pr: 0.5,
                  }}
                >
                  {selectedVessel &&
                    filteredTerminalCall &&
                    !containers.error &&
                    planType === PLAN_TYPE_LOADING &&
                    Object.entries(groupedUnpositionedContainers).flatMap(
                      ([terminalName, groupedContainers], index) => {
                      const term = searchTerm.toLowerCase();

                      const matchesUid = (container: any) => {
                        const { letters, numbers } = containerUidFormatter(
                          container.uid
                        );

                        return (
                          letters.toLowerCase().includes(term) ||
                          numbers.toLowerCase().includes(term) ||
                          container.uid.toLowerCase().includes(term)
                        );
                      };

                      const matchesLabels = (container: any) =>
                        filterLabels.length === 0 ||
                        container.labels?.some((label: any) =>
                          filterLabels.includes(label["@id"])
                        );

                      const matchesTypes = (container: any) =>
                        filterTypes.length === 0 ||
                        filterTypes.includes(container.containerType?.["@id"] || "");

                      const foundContainers = (
                        groupedContainers as Container[]
                      ).filter(
                        (container: any) =>
                          matchesLabels(container) ||
                          matchesTypes(container) ||
                          (searchTerm.length < 3 || matchesUid(container))
                      );

                      if (
                        searchTerm.length >= 3 ||
                        filterLabels.length > 0 ||
                        filterTypes.length > 0
                      ) {
                        return foundContainers.map(renderLoadingContainerGrid);
                      }

                      const emptyCount = foundContainers.filter(
                        (container: any) => container.empty
                      ).length;

                      return (
                        <Accordion key={terminalName} defaultExpanded>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel-${index}-content`}
                            id={`panel-${index}-header`}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              width="100%"
                            >
                              <Typography>
                                {terminalName} ({foundContainers.length})
                              </Typography>

                              {emptyCount && foundContainers.length ? (
                                <Typography variant="subtitle2">
                                  {t("empty")}:{" "}
                                  {`${emptyCount}/${foundContainers.length}`}
                                </Typography>
                              ) : null}
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails>
                            {foundContainers.map(renderLoadingContainerGrid)}
                          </AccordionDetails>
                        </Accordion>
                      );
                    }
                  )}
                </Box>
              </Grid>

              <Grid
                container
                component={Paper}
                gap={1}
                padding={1}
                sx={{
                  flex: "0 0 auto",
                  borderRadius: WRAPPER_BORDER_RADIUS,
                }}
              >
                <ContainerUploadField
                  name="containerUploadFile"
                  control={control}
                  register={register}
                  height="5vh"
                  callback={refreshPlannerAfterContainerUpload}
                />

                <Grid
                  container
                  justifyContent="space-between"
                  gap={1}
                  flexWrap="nowrap"
                >
                  <Button variant="outlined" fullWidth onClick={addContainer}>
                    <AddIcon />
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={addMultipleContainers}
                  >
                    <PlaylistAddIcon />
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={(event) =>
                      addTransferContainers(event, groupedUnpositionedContainers)
                    }
                  >
                    <AltRouteIcon />
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid
              className="container-planner-grid-main-wrapper"
              item
              xs={12}
              sm={10}
              sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
            >
              <Grid
                item
                className="container-planner-main-wrapper pdf-document-content"
                component={Paper}
                sx={{
                  borderRadius: WRAPPER_BORDER_RADIUS,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
{(currentCallHasContainersToLoad &&
                  (previousCallHasUnpositionedContainers ||
                    nextCallHasPositionedContainers ||
                    crossCallOverlaps.containersToUnposition.length > 0)) ||
                  (unpositionedContainerCount > 5 && !autoPositionAlertDismissed) ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: 8,
                      right: 8,
                      zIndex: 20,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {currentCallHasContainersToLoad &&
                    crossCallOverlaps.containersToUnposition.length > 0 ? (
                      <Alert
                        severity="error"
                        variant="outlined"
                        icon={<WarningAmberIcon fontSize="small" />}
                        action={
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              alignItems: "center",
                              alignSelf: "center",
                            }}
                          >
                            <Button
                              size="small"
                              color="error"
                              variant="contained"
                              disabled={Boolean(containers?.loading)}
                              onClick={unpositionCrossCallOverlapContainers}
                            >
                              {t("unpositionConflicting", {
                                count:
                                  crossCallOverlaps.containersToUnposition
                                    .length,
                              })}
                            </Button>
                          </Box>
                        }
                        sx={{
                          maxWidth: "none",
                          width: "100%",
                          backgroundColor: "rgba(255,255,255,0.92)",
                          alignItems: "flex-start",
                          "& .MuiAlert-message": {
                            p: 0,
                            flex: 1,
                          },
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: "0.8rem" }}>
                          {t("crossCallOverlapTitle")}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", opacity: 0.85 }}>
                          {t("crossCallOverlapSubtitle", {
                            count: crossCallOverlaps.containersToUnposition
                              .length,
                          })}
                        </Typography>
                      </Alert>
                    ) : null}

                    {currentCallHasContainersToLoad &&
                    previousCallHasUnpositionedContainers ? (
                      <Alert
                        severity="warning"
                        variant="outlined"
                        icon={<WarningAmberIcon fontSize="small" />}
                        sx={{
                          maxWidth: "none",
                          width: "100%",
                          backgroundColor: "rgba(255,255,255,0.92)",
                          alignItems: "flex-start",
                          "& .MuiAlert-message": {
                            p: 0,
                            flex: 1,
                          },
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: "0.8rem" }}>
                          {t("previousCallWarningTitle")}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", opacity: 0.85 }}>
                          {t("previousCallWarningSubtitle")}
                        </Typography>
                      </Alert>
                    ) : null}

                    {currentCallHasContainersToLoad &&
                    nextCallHasPositionedContainers &&
                    unpositionedContainerCount > 5 &&
                    !autoPositionAlertDismissed ? (
                      <Alert
                        severity="warning"
                        variant="outlined"
                        icon={<WarningAmberIcon fontSize="small" />}
                        sx={{
                          maxWidth: "none",
                          width: "100%",
                          backgroundColor: "rgba(255,255,255,0.92)",
                          alignItems: "flex-start",
                          "& .MuiAlert-message": {
                            p: 0,
                            flex: 1,
                          },
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: "0.8rem" }}>
                          {t("nextCallWarningTitle")}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", opacity: 0.85 }}>
                          {t("nextCallWarningSubtitle")}
                        </Typography>
                      </Alert>
                    ) : null}

                    {unpositionedContainerCount > 5 && !autoPositionAlertDismissed ? (
                      <Alert
                        severity="info"
                        variant="outlined"
                        icon={<PrecisionManufacturingIcon fontSize="small" />}
                        onClose={() => setAutoPositionAlertDismissed(true)}
                        action={
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              alignItems: "center",
                              alignSelf: "center",
                            }}
                          >
                            <Button
                              size="small"
                              color="primary"
                              variant="contained"
                              disabled={Boolean(containers?.loading)}
                              onClick={() => setShowAutoPositionConfirm(true)}
                            >
                              {t("autoPosition")}
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => setAutoPositionAlertDismissed(true)}
                              sx={{ color: "text.secondary" }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                        sx={{
                          maxWidth: "none",
                          width: "100%",
                          backgroundColor: "rgba(255,255,255,0.92)",
                          alignItems: "flex-start",
                          "& .MuiAlert-message": {
                            p: 0,
                            flex: 1,
                          },
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: "0.8rem" }}>
                          {t("autoPositionInfoTitle")}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", opacity: 0.85 }}>
                          {t("autoPositionInfoSubtitle")}
                        </Typography>
                      </Alert>
                    ) : null}
                  </Box>
                ) : null}
                {selectedVesselForPlanner &&
                  filteredTerminalCall &&
                  selectedVesselForPlanner.positions &&
                  selectedVesselForPlanner.positions.map((grids: any, layer: any) => (
                    <Grid
                      key={`layer-${layer}`}
                      className={`container-planner-wrapper ${
                        dockingSide === DOCKING_SIDE_STARBOARD
                          ? "container-planner-wrapper--revese"
                          : ""
                      } ${layer === 4 ? "hidden" : ""}`}
                      item
                    >
                      <Grid className="container-planner-ship-bow" item>
                        <Typography className="container-planner-layer-label">
                          {layer + 1}
                        </Typography>
                      </Grid>

                      <Grid className="container-planner-ship-body" item>
                        {grids &&
                          grids.map((gridCount: number, x: number) => (
                            <React.Fragment key={`ship-body-grid-${layer}-${x}`}>
                              {planType === PLAN_TYPE_SHIFTERS &&
                                shiftedContainers.map((container: any) =>
                                  container.shifterOriginal ? (
                                    <ShifterArrowConnector
                                      key={container["@id"]}
                                      fromId={`container-${normalizeId(
                                        container.shifterOriginal["@id"]
                                      )}`}
                                      toId={`container-${normalizeId(
                                        container["@id"]
                                      )}`}
                                    />
                                  ) : null
                                )}

                              <Grid
                                className="container-planner-ship-grid-wrapper"
                                item
                              >
                                <Typography className="container-planner-x-axis-label">
                                  {x + 1}
                                </Typography>

                                {[...Array(gridCount)].map((_, y) => {
                                  return RenderGrid(
                                    layer + 1,
                                    x + 1,
                                    y + 1,
                                    gridCount,
                                    grids,
                                    grids[x + 1]
                                  );
                                })}
                              </Grid>

                              {hasPositionBarrier([x + 1, x + 2]) ? (
                                <Grid className="container-planner-barrier" item />
                              ) : null}
                            </React.Fragment>
                          ))}
                      </Grid>
                    </Grid>
                  ))}

                {filteredTerminalCall &&
                selectedVesselForPlanner &&
                !selectedVesselForPlanner.positions ? (
                  <Grid item sx={{ display: "flex", justifyContent: "center" }}>
                    <Alert severity="warning">{t("noVesselGridPosition")}</Alert>
                  </Grid>
                ) : null}
              </Grid>

              {showPositionWeights &&
              selectedVesselForPlanner &&
              vesselPositionNumbers.length > 0 ? (
                <Grid
                  component={Paper}
                  sx={{
                    mt: 1,
                    borderRadius: WRAPPER_BORDER_RADIUS,
                    overflow: "hidden",
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.07)",
                  }}
                >
                  <Box
                    sx={(theme) => ({
                      px: 1.25,
                      py: 0.65,
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontSize: "0.75rem",
                      fontWeight: 950,
                      textTransform: "uppercase",
                      letterSpacing: "0.02em",
                    })}
                  >
                    {t("totalWeightTonPerPosition")}
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${vesselPositionNumbers.length}, minmax(54px, 1fr))`,
                      gap: 0,
                      px: 1,
                      py: 1,
                      overflowX: "auto",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    {vesselPositionNumbers.map((positionNumber) => {
                      const weight =
                        containerWeightPerPosition[positionNumber] ?? 0;

                      return (
                        <Box
                          key={positionNumber}
                          sx={{
                            minWidth: 54,
                            px: 0.5,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 950,
                              color: "primary.main",
                              lineHeight: 1.2,
                            }}
                          >
                            {positionNumber}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.65,
                              fontSize: "0.82rem",
                              fontWeight: 900,
                              color: "primary.main",
                              lineHeight: 1.2,
                            }}
                          >
                            {weight.toFixed(2)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
              ) : null}

              {departureStabilityState ? (
                <Grid
                  container
                  alignItems="center"
                  width="100%"
                  justifyContent="flex-start"
                  component={Paper}
                  padding={1}
                  gap={4}
                  sx={{
                    borderRadius: WRAPPER_BORDER_RADIUS,
                  }}
                >
                  <Box display="flex" gap={1}>
                    {(() => {
                      const terminalCall = updatedTerminalCall as any;
                      const vessel = selectedVesselForPlanner as any;

                      const state =
                        !vessel?.linkedVessel && vessel?.barge
                          ? terminalCall?.bargeDepartureState
                          : terminalCall?.departureState;

                      const stability = state?.stability;
                      const { z: loaded = 0 } = stability?.cog_loaded ?? {};
                      const { z: maxLoaded = 0 } = stability?.cog_loaded_max ?? {};

                      return (
                        <Button
                          color={
                            loaded > maxLoaded && maxLoaded !== 1
                              ? "error"
                              : "success"
                          }
                          variant="contained"
                          size="small"
                          onClick={toggleStability}
                        >
                          <Box display="flex" flexWrap="nowrap" gap={1}>
                            <Typography fontSize="small" fontWeight={700}>
                              {`${t("stability")}:`}
                            </Typography>
                            <Typography fontSize="small">
                              {`${loaded}/${maxLoaded}`}
                            </Typography>
                          </Box>
                        </Button>
                      );
                    })()}

                    <Button
                      color="primary"
                      variant="outlined"
                      size="small"
                      onClick={toggleStability}
                    >
                      <Box display="flex" flexWrap="nowrap" gap={2}>
                        <SideWeightIndicator
                          vessel={selectedVesselForPlanner}
                          containerWeights={containerGroupedByAxis(
                            selectedVesselForPlanner,
                            stabilityLoadingContainers,
                            filteredTerminalCall,
                            "y_axis"
                          )}
                          side="port"
                        />

                        <SideWeightIndicator
                          vessel={selectedVesselForPlanner}
                          containerWeights={containerGroupedByAxis(
                            selectedVesselForPlanner,
                            stabilityLoadingContainers,
                            filteredTerminalCall,
                            "y_axis"
                          )}
                          side="starboard"
                        />
                      </Box>
                    </Button>
                  </Box>

                  <Box display="flex" gap={1}>
                    {(() => {
                      const tonnage =
                        departureStabilityState?.statistics.total_weight;

                      return (
                        <Button
                          color={
                            tonnage === 0 ||
                            tonnage > (selectedVesselForPlanner as any)?.maxWeight
                              ? "error"
                              : "primary"
                          }
                          variant="outlined"
                          size="small"
                          onClick={toggleStability}
                        >
                          <Box display="flex" flexWrap="nowrap" gap={1}>
                            <Typography fontWeight={700} fontSize="small">
                              {`${t("tonnage")}:`}
                            </Typography>
                            <Typography fontSize="small">{tonnage}</Typography>
                          </Box>
                        </Button>
                      );
                    })()}

                    {(() => {
                      const maxDraught = getHighestDraughtForWaterType(
                        "fresh",
                        (selectedVesselForPlanner as any)?.totalWeightDraughtTable
                      );

                      return (
                        <Button
                          color={
                            departureStabilityState?.statistics?.draught === 0 ||
                            (maxDraught &&
                              departureStabilityState?.statistics?.draught >
                                maxDraught)
                              ? "error"
                              : "primary"
                          }
                          variant="outlined"
                          size="small"
                          onClick={toggleStability}
                        >
                          <Box display="flex" flexWrap="nowrap" gap={1}>
                            <Typography fontSize="small" fontWeight={700}>
                              {`${t("draught")}:`}
                            </Typography>
                            <Typography fontSize="small">
                              {`${departureStabilityState?.statistics?.draught}cm`}
                            </Typography>
                          </Box>
                        </Button>
                      );
                    })()}

                    {(() => {
                      const stackHeight = getVesselStackHeight(
                        (selectedVesselForPlanner as any)?.baseOffset,
                        departureStabilityState?.statistics?.draught ?? 0,
                        stabilityLoadingContainers
                      );

                      return (
                        <Button
                          color={stackHeight === 0 ? "error" : "primary"}
                          variant="outlined"
                          size="small"
                          onClick={toggleStability}
                        >
                          <Box display="flex" flexWrap="nowrap" gap={1}>
                            <Typography fontSize="small" fontWeight={700}>
                              {`${t("height")}:`}
                            </Typography>
                            <Typography fontSize="small">
                              {`${stackHeight.toFixed(2)}m`}
                            </Typography>
                          </Box>
                        </Button>
                      );
                    })()}
                  </Box>

                  <Box display="flex" gap={1}>
                    <Button
                      color="primary"
                      variant="outlined"
                      size="small"
                      onClick={toggleStability}
                    >
                      <Box display="flex" flexWrap="nowrap" gap={1}>
                        <Typography fontSize="small" fontWeight={700}>
                          {`${t("containers")}:`}
                        </Typography>
                        <Typography fontSize="small">
                          {`${stabilityLoadingContainers.length}`}
                        </Typography>
                      </Box>
                    </Button>

                    <Button color="primary" variant="outlined" size="small">
                      <Box display="flex" flexWrap="nowrap" gap={1}>
                        <Typography fontSize="small" fontWeight={700}>
                          {`${t("teu")}:`}
                        </Typography>
                        <Typography fontSize="small">
                          {`${departureStabilityState?.statistics?.total_teu}`}
                        </Typography>
                      </Box>
                    </Button>
                  </Box>
                </Grid>
              ) : null}
            </Grid>

            <Drawer
              anchor="right"
              open={stabilityOpen}
              onClose={closeStability}
              ModalProps={{ keepMounted: true }}
              sx={{
                "& .MuiDrawer-paper": {
                  width: 500,
                  boxSizing: "border-box",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1,
                  borderBottom: "1px solid rgba(0,0,0,0.12)",
                }}
              >
                <Typography fontWeight={700}>
                  {`${t("vesselStability")}:`}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title={t("generatePdf")} arrow>
                    <IconButton
                      onClick={() => setStabilityPreviewOpen(true)}
                      size="small"
                    >
                      <PictureAsPdfSharpIcon />
                    </IconButton>
                  </Tooltip>

                  <IconButton onClick={closeStability} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ p: 2, overflowY: "auto", height: "100%" }}>
                {filteredTerminalCall ? (
                  <VesselLoadStability
                    vessel={selectedVesselForPlanner}
                    containers={stabilityLoadingContainers}
                    terminalCall={updatedTerminalCall as any}
                  />
                ) : (
                  <Alert severity="warning">{t("selectPlanning")}</Alert>
                )}
              </Box>
            </Drawer>

            <MuiDialog
              open={stabilityPreviewOpen}
              onClose={() => setStabilityPreviewOpen(false)}
              maxWidth={false}
              PaperProps={{
                sx: {
                  width: "min(95vw, 960px)",
                  height: "auto",
                  maxHeight: "90vh",
                  borderRadius: 2,
                  m: 0,
                },
              }}
            >
              <DialogTitle sx={{ px: 2, py: 1.25 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 800 }}>
                    {t("vesselStability")}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Select
                      size="small"
                      value={documentLang}
                      onChange={(e) => setDocumentLang(e.target.value)}
                      sx={{
                        fontSize: "0.75rem",
                        minWidth: 90,
                        height: 32,
                        "& .MuiSelect-select": { py: 0.5 },
                      }}
                    >
                      <MenuItem value="en">EN</MenuItem>
                      <MenuItem value="nl">NL</MenuItem>
                      <MenuItem value="fr">FR</MenuItem>
                      <MenuItem value="de">DE</MenuItem>
                    </Select>

                    <Tooltip title={containers?.loading ? t("loadingContainers", "Loading containers...") : t("generatePdf")} arrow>
                      <Box component="span">
                        <Button
                          variant="contained"
                          size="small"
                          disabled={containers?.loading}
                          startIcon={containers?.loading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfSharpIcon />}
                          onClick={handleExportStabilityPdf}
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          {containers?.loading ? t("loadingContainers", "Loading containers...") : t("generatePdf")}
                        </Button>
                      </Box>
                    </Tooltip>

                    <IconButton
                      onClick={() => setStabilityPreviewOpen(false)}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>
              </DialogTitle>

              <DialogContent sx={{ p: 2, overflow: "auto" }}>
                <Box
                  ref={stabilityPdfRef}
                  sx={{
                    width: 760,
                    mx: "auto",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                  }}
                >
                  {filteredTerminalCall ? (
                    <VesselLoadStabilityDocument
                      vessel={selectedVesselForPlanner}
                      containers={stabilityLoadingContainers}
                      terminalCall={updatedTerminalCall as any}
                      language={documentLang}
                    />
                  ) : (
                    <Alert severity="warning">
                      {t("selectPlanning")}
                    </Alert>
                  )}
                </Box>
              </DialogContent>
            </MuiDialog>
          </Grid>

          {snackbar && (
            <Snackbar
              open
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              onClose={handleCloseSnackbar}
              autoHideDuration={10000}
            >
              <Alert {...snackbar} onClose={handleCloseSnackbar} />
            </Snackbar>
          )}

          {processing && (autoPlacing || totalCount > 0) && (
            <Snackbar
              open
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                icon={<CircularProgress size={20} color="inherit" />}
                onClose={() => {
                  autoPlaceCancelledRef.current = true;
                  setProcessing(false);
                  setAutoPlacing(false);
                }}
                severity="info"
                variant="filled"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  minWidth: 320,
                  maxWidth: 500,
                  bgcolor: "#0f172a",
                  color: "#f1f5f9",
                  boxShadow: "0 12px 32px rgba(2, 6, 23, 0.45)",
                  "& .MuiAlert-icon": { color: "#7dd3fc" },
                  "& .MuiAlert-action": { color: "#f1f5f9" },
                }}
              >
                <Stack spacing={1} sx={{ width: "100%" }}>
                  <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.84rem" }}>
                      {`${t(
                        "positioningContainers",
                        "Positioning containers..."
                      )} ${processedCount ?? 0}${
                        totalCount > 0 ? ` / ${totalCount}` : ""
                      }`}
                    </Typography>
                    <LinearProgress
                      variant={
                        totalCount > 0 ? "determinate" : "indeterminate"
                      }
                      value={
                        totalCount > 0
                          ? (processedCount / totalCount) * 100
                          : undefined
                      }
                      sx={{ mt: 0.75, borderRadius: 4 }}
                    />
                  </Box>

                  {appliedPlannerSettings && autoPlacing && (
                    <>
                      <Divider sx={{ borderColor: "rgba(255,255,255,.35)" }} />
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: "0.68rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                              opacity: 0.95,
                            }}
                          >
                            {t(
                              "autoPlannerSettingsApplied",
                              "Applied planner settings"
                            )}
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            icon={
                              <VerticalAlignTopOutlinedIcon
                                sx={{ color: "inherit" }}
                              />
                            }
                            label={t(
                              "autoPlannerConstraintRespectFortyOnTwenty",
                              "Respect 40 ft on top of 20 ft"
                            )}
                            size="small"
                            sx={SETTINGS_CHIP_SX}
                          />
                          {appliedPlannerSettings.constraints
                            ?.balanceOverBeam && (
                            <Chip
                              icon={
                                <BalanceOutlinedIcon
                                  sx={{ color: "inherit" }}
                                />
                              }
                              label={t(
                                "autoPlannerConstraintBalanceBeam",
                                "Balance over beam"
                              )}
                              size="small"
                              sx={SETTINGS_CHIP_SX}
                            />
                          )}
                          {appliedPlannerSettings.constraints
                            ?.balanceOverLength && (
                            <Chip
                              icon={
                                <DirectionsBoatOutlinedIcon
                                  sx={{ color: "inherit" }}
                                />
                              }
                              label={t(
                                "autoPlannerConstraintBalanceLength",
                                "Balance over length"
                              )}
                              size="small"
                              sx={SETTINGS_CHIP_SX}
                            />
                          )}
                          {appliedPlannerSettings.constraints?.segregateAdn && (
                            <Chip
                              icon={
                                <WarningAmberOutlinedIcon
                                  sx={{ color: "inherit" }}
                                />
                              }
                              label={t(
                                "autoPlannerConstraintSegregateAdn",
                                "Segregate ADN"
                              )}
                              size="small"
                              sx={SETTINGS_CHIP_SX}
                            />
                          )}
                          {appliedPlannerSettings.constraints?.avoidShifters && (
                            <Chip
                              icon={
                                <ViewColumnOutlinedIcon
                                  sx={{ color: "inherit" }}
                                />
                              }
                              label={t(
                                "autoPlannerConstraintAvoidShifters",
                                "Avoid shifters"
                              )}
                              size="small"
                              sx={SETTINGS_CHIP_SX}
                            />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: "0.72rem" }}>
                          {appliedPlannerSettings.exclusions > 0
                            ? `${appliedPlannerSettings.exclusions} ${t(
                                "autoPlannerSettingsExcluded",
                                "excluded"
                              )}`
                            : t(
                                "autoPlannerSettingsNoExclusions",
                                "No exclusions"
                              )}
                          {appliedPlannerSettings.sequence > 0
                            ? ` · ${appliedPlannerSettings.sequence} ${t(
                                "autoPlannerSettingsSequenced",
                                "sequenced"
                              )}`
                            : ""}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", opacity: 0.95 }}>
                          {t("autoPlannerSettingsGrouping", "Grouping")}:{" "}
                          {t(
                            `autoPlannerGrouping_${appliedPlannerSettings.groupingPreference}`,
                            appliedPlannerSettings.groupingPreference ===
                              "centerBlocks"
                              ? "Center blocks"
                              : appliedPlannerSettings.groupingPreference ===
                                  "smallPiles"
                                ? "Small piles"
                                : "Full beam strips"
                          )}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", opacity: 0.95 }}>
                          {t(
                            "autoPlannerSettingsIncomplete",
                            "Incomplete layers"
                          )}
                          :{" "}
                          {t(
                            `autoPlannerIncomplete_${appliedPlannerSettings.incompleteLayerPreference}`,
                            appliedPlannerSettings.incompleteLayerPreference ===
                              "outerRowsFirst"
                              ? "Outer rows first"
                              : "Middle rows first"
                          )}
                        </Typography>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Alert>
            </Snackbar>
          )}

          {isUnpositioning && (
            <Snackbar
              open
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                icon={<CircularProgress size={20} color="inherit" />}
                severity="info"
                variant="filled"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  minWidth: 320,
                  maxWidth: 500,
                  bgcolor: "#0f172a",
                  color: "#f1f5f9",
                  boxShadow: "0 12px 32px rgba(2, 6, 23, 0.45)",
                  "& .MuiAlert-icon": { color: "#7dd3fc" },
                }}
              >
                <Stack spacing={1} sx={{ width: "100%" }}>
                  <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.84rem" }}>
                      {t(
                        "unpositioningContainers",
                        "Unpositioning containers..."
                      )}
                    </Typography>
                    <LinearProgress
                      variant="indeterminate"
                      sx={{ mt: 0.75, borderRadius: 4 }}
                    />
                  </Box>
                </Stack>
              </Alert>
            </Snackbar>
          )}

          <Dialog />
          <Modal />

          <FilterMenu
            anchorEl={filterAnchor}
            open={Boolean(filterAnchor)}
            onClose={handleApplyFilters}
            labels={containerLabels.map((label: any) => ({
              id: label["@id"],
              name: label.name,
            }))}
            types={containerDataTypes.map((type: any) => ({
              id: type["@id"],
              name: type.name,
            }))}
            selectedLabels={activeFilters.labels}
            selectedTypes={activeFilters.types}
          />

          <PlannerViewSettings
            anchorEl={settingsAnchor}
            open={Boolean(settingsAnchor)}
            onClose={handleCloseSettings}
            containerColorType={containerColorType}
            setContainerColorType={setContainerColorType}
            positionModeEnabled={positionModeEnabled}
            setPositionModeEnabled={setPositionModeEnabled}
            shiftingModeEnabled={shiftingModeEnabled}
            setShiftingModeEnabled={setShiftingModeEnabled}
            containerGridLabelSwitch={containerGridLabelSwitch}
            setContainerGridLabelSwitch={setContainerGridLabelSwitch}
          />

          <MuiDialog
            open={sameTimeDialogOpen}
            onClose={() => setSameTimeDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", gap: 1 }}>
              <WarningAmberIcon sx={{ color: "#d97706", fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 800 }}>
                {t("duplicateTimings")}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                {t("duplicateTimingsDescription")}
              </Typography>
              {duplicateGroups.map((group, gi) => (
                <Box key={gi} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75, fontSize: "0.78rem" }}>
                    {group.dateLabel}
                  </Typography>
                  <Stack spacing={0.6}>
                    {group.calls.map((tc, ci) => {
                      const label = getTerminalCallPrimaryLabel(tc);
                      const terminalName = tc?.terminal?.name || t("unknown");
                      const isFirst = ci === 0;
                      const terminalColor = getPlannerTerminalCallColor(tc);
                      return (
                        <Paper
                          key={tc["@id"]}
                          elevation={0}
                          sx={{
                            p: 0.65,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.65,
                            borderRadius: "10px",
                            backgroundColor: (theme) =>
                              isFirst
                                ? alpha(theme.palette.common.black, 0.03)
                                : alpha(terminalColor, 0.12),
                            border: (theme) =>
                              isFirst
                                ? "1px solid rgba(0,0,0,0.08)"
                                : `1px solid ${alpha(terminalColor, 0.35)}`,
                          }}
                        >
                          <Box
                            sx={(theme) => ({
                              width: 26,
                              height: 26,
                              borderRadius: "50%",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: isFirst
                                ? `2px solid ${alpha(theme.palette.common.black, 0.12)}`
                                : `2px solid ${alpha(terminalColor, 0.5)}`,
                              backgroundColor: isFirst
                                ? alpha(theme.palette.common.black, 0.04)
                                : alpha(terminalColor, 0.18),
                            })}
                          >
                            {isFirst ? (
                              <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                            ) : (
                              <WarningAmberIcon sx={{ fontSize: 14, color: "#b45309" }} />
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "0.78rem",
                                fontWeight: 800,
                                lineHeight: 1.2,
                                color: isFirst ? "text.primary" : "#92400e",
                              }}
                            >
                              {label || terminalName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.66rem",
                                fontWeight: 600,
                                color: isFirst ? "text.secondary" : "#b45309",
                                mt: 0.15,
                              }}
                            >
                              {isFirst
                                ? terminalName
                                : `${dayjs(tc?.planned || tc?.arrival)
                                    .add(timeOffsets[tc["@id"]] ?? ci * 10, "minute")
                                    .format("HH:mm")} — ${terminalName}`}
                            </Typography>
                          </Box>
                          {!isFirst && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={timeOffsets[tc["@id"]] ?? ci * 10}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  if (!isNaN(val) && val >= 1) {
                                    setTimeOffsets((prev) => {
                                      const next = { ...prev, [tc["@id"]]: val };
                                      const nextCall = ci < group.calls.length - 1 ? group.calls[ci + 1] : null;
                                      if (nextCall) {
                                        const nextVal = next[nextCall["@id"]] ?? (ci + 1) * 10;
                                        if (nextVal <= val) next[nextCall["@id"]] = val + 1;
                                      }
                                      const prevCall = ci > 1 ? group.calls[ci - 1] : null;
                                      if (prevCall) {
                                        const prevVal = next[prevCall["@id"]] ?? (ci - 1) * 10;
                                        if (prevVal >= val) next[prevCall["@id"]] = val - 1;
                                      }
                                      return next;
                                    });
                                  }
                                }}
                                inputProps={{
                                  min: (() => {
                                    const prevCall = ci > 1 ? group.calls[ci - 1] : null;
                                    return prevCall ? (timeOffsets[prevCall["@id"]] ?? (ci - 1) * 10) + 1 : 1;
                                  })(),
                                  max: (() => {
                                    const nextCall = ci < group.calls.length - 1 ? group.calls[ci + 1] : null;
                                    return nextCall ? (timeOffsets[nextCall["@id"]] ?? (ci + 1) * 10) - 1 : 60;
                                  })(),
                                  style: {
                                    width: 30,
                                    padding: "2px 4px",
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    textAlign: "center",
                                  },
                                }}
                                sx={{
                                  width: 46,
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "7px",
                                    backgroundColor: "rgba(255,255,255,0.85)",
                                    "& fieldset": {
                                      borderColor: "rgba(217,119,6,0.3)",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "rgba(217,119,6,0.5)",
                                    },
                                  },
                                }}
                              />
                              <Typography sx={{ fontSize: "0.6rem", fontWeight: 800, color: "#b45309" }}>
                                min
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.25, gap: 1 }}>
              <Button onClick={() => setSameTimeDialogOpen(false)} size="small">
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                size="small"
                color="warning"
                startIcon={<WarningAmberIcon />}
                onClick={() => {
                  setShowConfirmDialog(true);
                }}
              >
                {t("add10MinToDuplicates")}
              </Button>
            </DialogActions>
          </MuiDialog>

            <MuiDialog
            open={showConfirmDialog}
            onClose={() => setShowConfirmDialog(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ px: 2, py: 1.25, fontSize: "1rem", fontWeight: 800 }}>
              {t("confirm")}
            </DialogTitle>
            <DialogContent sx={{ px: 2, py: 1 }}>
              <Typography variant="body2">
                {t("confirmAdd10Min")}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.25, gap: 1 }}>
              <Button onClick={() => setShowConfirmDialog(false)} size="small">
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                size="small"
                color="warning"
                onClick={fixDuplicateTimings}
              >
                {t("confirm")}
              </Button>
            </DialogActions>
          </MuiDialog>

          <MuiDialog
            open={showUnpositionConfirm}
            onClose={() => setShowUnpositionConfirm(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ px: 2, py: 1.25, fontSize: "1rem", fontWeight: 800 }}>
              {t("reset")}
            </DialogTitle>
            <DialogContent sx={{ px: 2, py: 1 }}>
              <Typography variant="body2">
                {t("confirmUnpositionAll")}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.25, gap: 1 }}>
              <Button onClick={() => setShowUnpositionConfirm(false)} size="small">
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                size="small"
                color="warning"
                onClick={() => {
                  setShowUnpositionConfirm(false);
                  unpositionAllTerminalCallContainers();
                }}
              >
                {t("confirm")}
              </Button>
            </DialogActions>
          </MuiDialog>

          <AutoPlannerDialog
            open={showAutoPositionConfirm}
            vessel={selectedVesselForPlanner}
            terminalCall={filteredTerminalCall}
            planningContainers={containerData}
            allContainers={containers.data || []}
            nextTerminalCall={nextTerminalCallForPlanner}
            processing={processing}
            onClose={() => setShowAutoPositionConfirm(false)}
            onVesselUpdated={(updatedVessel) => {
              setSelectedVesselForPlanner(updatedVessel);
              if (
                normalizeId(selectedVessel?.["@id"]) ===
                normalizeId(updatedVessel?.["@id"])
              ) {
                setSelectedVessel(updatedVessel);
              }
            }}
            onConfirm={(maximumLayers) => {
              setShowAutoPositionConfirm(false);
              autoPlaceRemainingContainers(maximumLayers);
            }}
          />

          <MuiDialog
            open={showStabilityReorgConfirm}
            onClose={() => handleStabilityReorgConfirm(false)}
          >
            <DialogTitle sx={{ px: 2, py: 1.25, fontSize: "1rem", fontWeight: 800 }}>
              {t("autoPlannerStabilityReorgTitle")}
            </DialogTitle>
            <DialogContent sx={{ px: 2, py: 1 }}>
              <Typography variant="body2">
                {t("autoPlannerStabilityReorgBody")}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.25, gap: 1 }}>
              <Button onClick={() => handleStabilityReorgConfirm(false)} variant="outlined" sx={{ textTransform: "none" }}>
                {t("cancel", "Cancel")}
              </Button>
              <Button onClick={() => handleStabilityReorgConfirm(true)} variant="contained" sx={{ textTransform: "none" }}>
                {t("continue", "Continue")}
              </Button>
            </DialogActions>
          </MuiDialog>

        </Box>
      </Box>
    </Layout>
  );
};

export default ContainerPlanner;
